import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DownloadIcon from '@mui/icons-material/Download';
import InsightsIcon from '@mui/icons-material/Insights';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { alpha } from '@mui/material/styles';

import Layout from './Layout';
import {
  downloadTestDispatchKpiReport,
  getDispatchControlBoard,
  getDispatchControlBoardSettings,
  getHourlyHeatmap,
  getKpiClients,
  sendTestDispatchKpiReport,
  updateDispatchControlBoardSettings,
} from '../services/kpiGenericApi';
import { editClient, getClients } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';

const DEFAULT_OPERATOR_TARGET = 20;
const DEFAULT_WORKING_HOURS_PER_DAY = 9.5;
const DEFAULT_DAILY_KPI_REPORT_TIME = '18:00';
const MIN_DISPATCH_TARGET = 1;
const MAX_DISPATCH_TARGET = 500;
const MIN_WORKING_HOURS = 1;
const MAX_WORKING_HOURS = 24;
const AUTO_REFRESH_MS = 60000;
const TEST_KPI_REPORT_EMAIL = 'hoyin@iv-creative.co.uk';
const REPORT_TIME_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
const ALL_CLIENTS_REPORT_KEY = 'ALL_CLIENTS_OVERALL';
const DEFAULT_ALL_CLIENT_EXCLUDED_CLIENTS = [];
const CLIENT_EXCLUSION_PARAM_SEPARATOR = '|';
const HEATMAP_STATS = ['received', 'dispatched', 'todo'];
const HEATMAP_LABELS = {
  received: 'Received',
  dispatched: 'Dispatched',
  todo: 'To-Do',
};
const HEATMAP_BASE_COLORS = {
  received: '#037ec7',
  dispatched: '#4CBF88',
  todo: '#F0B354',
};
const STATUS_LABELS = {
  AHEAD: 'Ahead',
  ON_PACE: 'On Pace',
  BEHIND: 'Behind',
  NEUTRAL: 'Neutral',
  HEALTHY: 'Healthy',
  WATCH: 'Watch',
  RISK: 'Risk',
};

const PRODUCTIVITY_STATUS_LABELS = {
  NEUTRAL: 'Neutral',
  HEALTHY: 'On Target',
  WATCH: 'Near Target',
  RISK: 'Below Target',
};

function getRelativeLocalDateInputValue(daysOffset = 0) {
  const now = new Date();
  now.setDate(now.getDate() + daysOffset);
  const local = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
  return local.toISOString().slice(0, 10);
}

function getLocalDateInputValue() {
  return getRelativeLocalDateInputValue(0);
}

function getYesterdayDateInputValue() {
  return getRelativeLocalDateInputValue(-1);
}

function formatNumber(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return '--';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value));
}

function formatPercent(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return '--';
  return `${formatNumber(value, digits)}%`;
}

function formatRate(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return '--';
  return `${formatNumber(value, digits)}/hr`;
}

function formatHours(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return '--';
  return `${formatNumber(value, digits)}h`;
}

function formatHoursWindowFraction(value, workingHoursPerDay, workingHoursDigits = 1) {
  if (value == null || Number.isNaN(Number(value))) return '--';
  const normalizedWorkingHours = Number(workingHoursPerDay);
  if (!Number.isFinite(normalizedWorkingHours) || normalizedWorkingHours <= 0) {
    return formatNumber(value);
  }
  return `${formatNumber(value)}/${formatNumber(normalizedWorkingHours, workingHoursDigits)}h`;
}

function formatFraction(numerator, denominator) {
  if (numerator == null && denominator == null) return '--';
  return `${formatNumber(numerator)}/${formatNumber(denominator)}`;
}

function formatDelta(value) {
  if (value == null || Number.isNaN(Number(value))) return '--';
  const numeric = Number(value);
  return `${numeric > 0 ? '+' : ''}${formatNumber(numeric, 1)}`;
}

function formatDateTime(value) {
  if (!value) return '--';
  const raw = String(value).trim();
  const hasExplicitTimezone = /(?:[zZ]|[+-]\d{2}:\d{2})$/.test(raw);
  const localMatch = !hasExplicitTimezone
    ? raw.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/)
    : null;

  if (localMatch) {
    const [, year, month, day, hour, minute] = localMatch;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatLastUpdated(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

function formatHourLabel(hourBucket, includeDay = false) {
  if (!hourBucket) return '--';
  const [dayPart, timePart] = String(hourBucket).split(' ');
  if (!dayPart || !timePart) return String(hourBucket);
  return includeDay ? `${dayPart.slice(5)} ${timePart}` : timePart;
}

function normalizeOperatorKey(value) {
  return String(value || 'IVuser').trim().toLowerCase();
}

function csvEscape(value) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

function getPriorityScopeSuffix(priority) {
  if (priority === 'EXPRESS') return ' (Express)';
  if (priority === 'STANDARD') return ' (Standard)';
  return '';
}

function getExceptionStatusLabel(row) {
  const priority = String(row?.shippingPriority || '').trim().toLowerCase();
  if (row?.type === 'AT_RISK') {
    return priority === 'express' ? 'At Risk - Due Today' : 'At Risk - Due Soon';
  }
  return 'Late - Past Due';
}

function buildTargetDraftMap(rows = []) {
  return rows.reduce((accumulator, row) => {
    accumulator[row.clientId] = String(row.dispatchTargetPerHour ?? DEFAULT_OPERATOR_TARGET);
    return accumulator;
  }, {});
}

function sanitizeDispatchTargetInput(value) {
  return String(value ?? '').replace(/[^\d]/g, '');
}

function sanitizeWorkingHoursInput(value) {
  const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
  if (!cleaned) return '';
  const [whole, ...decimalParts] = cleaned.split('.');
  if (!decimalParts.length) return whole;
  return `${whole}.${decimalParts.join('').slice(0, 1)}`;
}

function normalizeWorkingHoursValue(value, fallback = DEFAULT_WORKING_HOURS_PER_DAY) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < MIN_WORKING_HOURS) return fallback;
  return Math.min(parsed, MAX_WORKING_HOURS);
}

function buildEmptyReportScheduleDraft(overrides = {}) {
  return {
    enabled: false,
    recipients: '',
    time: DEFAULT_DAILY_KPI_REPORT_TIME,
    ...overrides,
  };
}

function buildClientReportScheduleDraft(row = {}) {
  return buildEmptyReportScheduleDraft({
    enabled: Boolean(row.dailyKpiReportEnabled),
    recipients: String(row.dailyKpiReportRecipients || ''),
    time: normalizeReportTimeValue(row.dailyKpiReportTime),
  });
}

function buildAllClientsReportScheduleDraft(settings = {}) {
  return buildEmptyReportScheduleDraft({
    enabled: Boolean(settings?.allClientsReportEnabled),
    recipients: String(settings?.allClientsReportRecipients || ''),
    time: normalizeReportTimeValue(settings?.allClientsReportTime),
  });
}

function getDailyDigestReportTimeValue(settings = {}) {
  return normalizeReportTimeValue(settings?.dailyDigestReportTime);
}

function areReportScheduleDraftsEqual(left = {}, right = {}) {
  return (
    Boolean(left?.enabled) === Boolean(right?.enabled)
    && normalizeReportRecipientsValue(left?.recipients) === normalizeReportRecipientsValue(right?.recipients)
    && normalizeReportTimeValue(left?.time) === normalizeReportTimeValue(right?.time)
  );
}

function buildReportScheduleDraftMap(rows = []) {
  return rows.reduce((accumulator, row) => {
    accumulator[row.clientId] = buildClientReportScheduleDraft(row);
    return accumulator;
  }, {});
}

function parseReportRecipientList(value) {
  return String(value || '')
    .split(/[\n\r,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function getInvalidReportRecipients(value) {
  return parseReportRecipientList(value).filter((entry) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(entry));
}

function normalizeReportRecipientsValue(value) {
  return parseReportRecipientList(value).join('; ');
}

function normalizeReportTimeValue(value) {
  const normalized = String(value || '').trim();
  return REPORT_TIME_REGEX.test(normalized) ? normalized : DEFAULT_DAILY_KPI_REPORT_TIME;
}

function isValidReportTimeValue(value) {
  return REPORT_TIME_REGEX.test(String(value || '').trim());
}

function formatReportPreviewDateValue(value) {
  const normalized = String(value || '').trim();
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return normalized || '--';
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

function normalizeClientOptions(items = []) {
  return items
    .map((item) => {
      if (typeof item === 'string') {
        return {
          clientName: item,
          dispatchTargetPerHour: DEFAULT_OPERATOR_TARGET,
        };
      }

      if (!item?.clientName) return null;

      return {
        clientName: item.clientName,
        dispatchTargetPerHour: Number(item.dispatchTargetPerHour) > 0
          ? Number(item.dispatchTargetPerHour)
          : DEFAULT_OPERATOR_TARGET,
      };
    })
    .filter(Boolean);
}

function getAllClientExclusionParam(clientName, excludedClientNames = []) {
  if (clientName !== 'ALL') return '';
  return excludedClientNames
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .join(CLIENT_EXCLUSION_PARAM_SEPARATOR);
}

function formatExcludedClientsLabel(excludedClientNames = []) {
  if (!excludedClientNames.length) return 'None';
  if (excludedClientNames.length <= 2) return excludedClientNames.join(', ');
  return `${excludedClientNames.slice(0, 2).join(', ')} +${excludedClientNames.length - 2}`;
}

function formatTargetPercent(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return '--';
  return formatPercent(Number(value) * 100, digits);
}

function formatDateRangeLabel(startDate, endDate) {
  if (!startDate && !endDate) return '--';
  if (startDate && endDate && startDate !== endDate) return `${startDate} to ${endDate}`;
  return startDate || endDate;
}

function getTargetTone(value) {
  if (value == null || Number.isNaN(Number(value))) return 'neutral';
  if (Number(value) < 0.75) return 'error';
  if (Number(value) < 1) return 'warning';
  return 'success';
}

function getCardTone(tone) {
  switch (tone) {
    case 'success':
      return {
        borderColor: 'success.light',
        backgroundColor: 'rgba(76, 175, 80, 0.08)',
      };
    case 'warning':
      return {
        borderColor: 'warning.light',
        backgroundColor: 'rgba(255, 167, 38, 0.08)',
      };
    case 'error':
      return {
        borderColor: 'error.light',
        backgroundColor: 'rgba(244, 67, 54, 0.08)',
      };
    default:
      return {
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      };
  }
}

function getStatusChipStyles(status) {
  switch (status) {
    case 'AHEAD':
    case 'HEALTHY':
      return { backgroundColor: 'rgba(46, 125, 50, 0.12)', color: '#1b5e20' };
    case 'ON_PACE':
    case 'WATCH':
      return { backgroundColor: 'rgba(245, 124, 0, 0.12)', color: '#ad5d00' };
    case 'BEHIND':
    case 'RISK':
      return { backgroundColor: 'rgba(211, 47, 47, 0.12)', color: '#b71c1c' };
    default:
      return { backgroundColor: 'rgba(97, 97, 97, 0.12)', color: '#4a4a4a' };
  }
}

function getIssueStatusChipStyles(status) {
  switch (String(status || '').trim().toUpperCase()) {
    case 'ON HOLD':
      return { backgroundColor: 'rgba(211, 47, 47, 0.12)', color: '#b71c1c' };
    case 'URGENT':
      return { backgroundColor: 'rgba(198, 40, 40, 0.16)', color: '#8e0000' };
    case 'IN PROGRESS':
      return { backgroundColor: 'rgba(245, 124, 0, 0.12)', color: '#ad5d00' };
    default:
      return { backgroundColor: 'rgba(97, 97, 97, 0.12)', color: '#4a4a4a' };
  }
}

function compareSortValues(a, b, direction) {
  const dir = direction === 'asc' ? 1 : -1;
  const isNilA = a == null;
  const isNilB = b == null;
  if (isNilA && isNilB) return 0;
  if (isNilA) return 1;
  if (isNilB) return -1;
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b) * dir;
  return (a - b) * dir;
}

function SummaryMetricCard({
  icon,
  title,
  value,
  valueAdornment = null,
  caption,
  helper,
  tone = 'neutral',
  banded = false,
  isLast = false,
}) {
  const toneStyles = getCardTone(tone);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.45, lg: 1.18, xl: banded ? 0.92 : 1.08 },
        borderRadius: { xs: 3, xl: banded ? 0 : 3 },
        minHeight: { xs: 98, lg: 84, xl: banded ? 74 : 80 },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 0.5, xl: 0.42 },
        position: 'relative',
        overflow: 'hidden',
        borderColor: { xs: toneStyles.borderColor, xl: banded ? 'transparent' : toneStyles.borderColor },
        borderRight: { xl: banded && !isLast ? '1px solid rgba(15, 23, 42, 0.08)' : 'none' },
        backgroundColor: toneStyles.backgroundColor,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 100%)',
        }}
      />
      {banded ? (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: 2,
            backgroundColor: toneStyles.borderColor,
            opacity: 0.9,
          }}
        />
      ) : null}
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              fontWeight: 700,
              fontSize: { xs: 10, xl: 9.5 },
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            width: { xs: 24, xl: 22 },
            height: { xs: 24, xl: 22 },
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.82)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            color: 'text.primary',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.02, fontSize: { xs: 19, lg: 17.5, xl: 17 } }}>
          {value}
        </Typography>
        {valueAdornment}
      </Box>
      <Stack spacing={0.3} sx={{ position: 'relative' }}>
        {caption ? (
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: { xs: 11.5, xl: 11.1 }, lineHeight: 1.2 }}>
            {caption}
          </Typography>
        ) : null}
        {helper ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: 10.1, xl: 9.7 }, lineHeight: 1.18 }}>
            {helper}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function getOperatorGroupDividerSx() {
  return {
    borderRight: '2px solid rgba(15, 23, 42, 0.12)',
  };
}

function StatusChip({ value, labels = STATUS_LABELS, chipProps = {} }) {
  const normalized = String(value || 'NEUTRAL').toUpperCase();
  const { sx: chipSx, ...restChipProps } = chipProps;
  return (
    <Chip
      size="small"
      label={labels[normalized] || normalized}
      {...restChipProps}
      sx={{
        fontWeight: 700,
        ...getStatusChipStyles(normalized),
        ...(chipSx || {}),
      }}
    />
  );
}

function SummaryStripSkeleton() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
          lg: 'repeat(4, minmax(0, 1fr))',
          xl: 'repeat(8, minmax(0, 1fr))',
        },
        gap: { xs: 1.2, lg: 0.9, xl: 0 },
        borderRadius: { xl: 3 },
        overflow: { xl: 'hidden' },
        border: { xl: '1px solid rgba(15, 23, 42, 0.08)' },
        backgroundColor: { xl: 'rgba(255,255,255,0.96)' },
      }}
    >
      {Array.from({ length: 8 }).map((_, index) => (
        <Paper
          key={index}
          variant="outlined"
          sx={{
            p: { xs: 2, xl: 1.1 },
            borderRadius: { xs: 3, xl: 0 },
            borderColor: { xs: 'divider', xl: 'transparent' },
            borderRight: { xl: index < 7 ? '1px solid rgba(15, 23, 42, 0.08)' : 'none' },
          }}
        >
          <Skeleton width="45%" height={20} />
          <Skeleton width="70%" height={42} />
          <Skeleton width="85%" height={18} />
          <Skeleton width="60%" height={18} />
        </Paper>
      ))}
    </Box>
  );
}

function ControlGroup({ label, children, emphasis = false }) {
  return (
    <Box
      sx={{
        p: { xs: 1, xl: 0.9 },
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: emphasis ? 'primary.light' : 'divider',
        backgroundColor: emphasis ? 'rgba(25, 118, 210, 0.05)' : 'rgba(248, 250, 252, 0.92)',
        minHeight: { xs: 90, lg: 84 },
        '& .MuiInputBase-root': {
          minHeight: 34,
          fontSize: 12.5,
        },
        '& .MuiToggleButton-root': {
          minHeight: 31,
          px: 1.05,
          fontSize: 11.5,
          lineHeight: 1.1,
        },
        '& .MuiButton-root': {
          minHeight: 31,
          fontSize: 11.5,
          px: 1.15,
        },
        '& .MuiFormControlLabel-label': {
          fontSize: 12.25,
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.5,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.65,
          fontWeight: 700,
          fontSize: 10.5,
        }}
      >
        {label}
      </Typography>
      <Stack spacing={0.75}>
        {children}
      </Stack>
    </Box>
  );
}

function SectionHeader({ eyebrow, title, subtitle, action }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: { xs: 1, md: 1.25 },
        flexWrap: 'wrap',
        alignItems: { xs: 'flex-start', md: 'center' },
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
        {eyebrow ? (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 0.35,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.7,
              fontWeight: 700,
              fontSize: 10.5,
            }}
          >
            {eyebrow}
          </Typography>
        ) : null}
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.08, fontSize: { xs: 16, md: 17 } }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, md: 12.5 }, lineHeight: 1.35 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action ? (
        <Box
          sx={{
            width: { xs: '100%', md: 'auto' },
            minWidth: 0,
            flex: '0 0 auto',
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'flex-end',
            mt: { xs: 0.2, md: 0 },
          }}
        >
          {action}
        </Box>
      ) : null}
    </Box>
  );
}

export default function KpiHourlyAndDispatchDashboard({ clients = [] }) {
  const { permissions } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const isLaptopWidth = useMediaQuery('(max-width:1600px)');
  const hideSecondaryTableColumns = isLaptopWidth;
  const canManageClients = Array.isArray(permissions) && permissions.includes('ManageClients');
  const canManageUsers = Array.isArray(permissions) && permissions.includes('ManageUsers');
  const [client, setClient] = useState('ALL');
  const [clientList, setClientList] = useState(() => normalizeClientOptions(clients));
  const [excludedAllClientNames, setExcludedAllClientNames] = useState(DEFAULT_ALL_CLIENT_EXCLUDED_CLIENTS);
  const [lens, setLens] = useState('today');
  const [startDate, setStartDate] = useState(getLocalDateInputValue());
  const [endDate, setEndDate] = useState(getLocalDateInputValue());
  const [windowHours, setWindowHours] = useState(24);
  const [priority, setPriority] = useState('ALL');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [operatorQuery, setOperatorQuery] = useState('');
  const [operatorActivityFilter, setOperatorActivityFilter] = useState('all');
  const [operatorSortBy, setOperatorSortBy] = useState('intervention');
  const [operatorSortDir, setOperatorSortDir] = useState('asc');

  const [boardLoading, setBoardLoading] = useState(true);
  const [boardError, setBoardError] = useState('');
  const [controlBoard, setControlBoard] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [targetManagerOpen, setTargetManagerOpen] = useState(false);
  const [targetManagerLoading, setTargetManagerLoading] = useState(false);
  const [targetManagerSaving, setTargetManagerSaving] = useState(false);
  const [targetManagerError, setTargetManagerError] = useState('');
  const [targetManagerSearch, setTargetManagerSearch] = useState('');
  const [targetManagerListFilter, setTargetManagerListFilter] = useState('all');
  const [targetManagerClients, setTargetManagerClients] = useState([]);
  const [targetDrafts, setTargetDrafts] = useState({});
  const [selectedTargetClientId, setSelectedTargetClientId] = useState(null);
  const [reportManagerOpen, setReportManagerOpen] = useState(false);
  const [reportManagerLoading, setReportManagerLoading] = useState(false);
  const [reportManagerSaving, setReportManagerSaving] = useState(false);
  const [reportManagerError, setReportManagerError] = useState('');
  const [reportManagerSearch, setReportManagerSearch] = useState('');
  const [reportManagerListFilter, setReportManagerListFilter] = useState('all');
  const [reportManagerClients, setReportManagerClients] = useState([]);
  const [reportScheduleDrafts, setReportScheduleDrafts] = useState({});
  const [selectedReportConfigId, setSelectedReportConfigId] = useState(ALL_CLIENTS_REPORT_KEY);
  const [dailyDigestReportTimeDraft, setDailyDigestReportTimeDraft] = useState(DEFAULT_DAILY_KPI_REPORT_TIME);
  const [savedDailyDigestReportTime, setSavedDailyDigestReportTime] = useState(DEFAULT_DAILY_KPI_REPORT_TIME);
  const [allClientsReportDraft, setAllClientsReportDraft] = useState(buildAllClientsReportScheduleDraft());
  const [savedAllClientsReportDraft, setSavedAllClientsReportDraft] = useState(buildAllClientsReportScheduleDraft());
  const [reportPreviewDate, setReportPreviewDate] = useState(getLocalDateInputValue());
  const [reportTestSendingClientId, setReportTestSendingClientId] = useState(null);
  const [reportTestDownloadingClientId, setReportTestDownloadingClientId] = useState(null);
  const [selectedOperatorDrilldown, setSelectedOperatorDrilldown] = useState(null);
  const [workingHoursDraft, setWorkingHoursDraft] = useState('');
  const [appliedWorkingHours, setAppliedWorkingHours] = useState('');
  const [savedWorkingHoursPerDay, setSavedWorkingHoursPerDay] = useState(null);
  const [workingHoursSaving, setWorkingHoursSaving] = useState(false);

  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapError, setHeatmapError] = useState('');
  const [heatmapData, setHeatmapData] = useState({ hours: [], categories: [] });

  const selectedClientConfig = useMemo(
    () => clientList.find((item) => item.clientName === client) || null,
    [client, clientList]
  );
  const allClientExclusionOptions = useMemo(() => {
    const optionMap = new Map();
    [...clientList.map((item) => item.clientName), ...excludedAllClientNames]
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .forEach((item) => optionMap.set(item.toLowerCase(), item));
    return Array.from(optionMap.values()).sort((left, right) => left.localeCompare(right));
  }, [clientList, excludedAllClientNames]);
  const activeExcludedAllClientNames = useMemo(
    () => (client === 'ALL' ? excludedAllClientNames : []),
    [client, excludedAllClientNames]
  );
  const excludedClientsParam = useMemo(
    () => getAllClientExclusionParam(client, activeExcludedAllClientNames),
    [activeExcludedAllClientNames, client]
  );
  const hasTarget = true;
  const operatorTargetPerHour = selectedClientConfig?.dispatchTargetPerHour ?? DEFAULT_OPERATOR_TARGET;
  const livePacing = controlBoard?.summary?.teamCurrentHourOrders != null;
  const showActiveOperatorControls = lens === 'today';
  const heatmapAvailable = !['selected-day', 'yesterday'].includes(lens);
  const heatmapHours = lens === 'rolling' ? windowHours : 24;
  const priorityScopeSuffix = getPriorityScopeSuffix(priority);
  const controlBoardHasTarget = controlBoard?.meta?.hasTarget ?? hasTarget;
  const effectiveSavedWorkingHours = savedWorkingHoursPerDay ?? controlBoard?.meta?.workingHoursPerDay ?? DEFAULT_WORKING_HOURS_PER_DAY;
  const operatorTableColumnCount = hideSecondaryTableColumns
    ? (livePacing ? 10 : 8)
    : (livePacing ? 12 : 10);
  const exceptionTableColumnCount = hideSecondaryTableColumns ? 8 : 9;
  const havingIssueTableColumnCount = 7;

  const applyDispatchSettingsState = useCallback((settings = {}) => {
    const normalizedWorkingHours = normalizeWorkingHoursValue(settings?.workingHoursPerDay);
    const normalizedDailyDigestReportTime = getDailyDigestReportTimeValue(settings);
    const normalizedAllClientsReportDraft = buildAllClientsReportScheduleDraft(settings);
    setSavedWorkingHoursPerDay(normalizedWorkingHours);
    setWorkingHoursDraft(String(normalizedWorkingHours));
    setAppliedWorkingHours(String(normalizedWorkingHours));
    setSavedDailyDigestReportTime(normalizedDailyDigestReportTime);
    setDailyDigestReportTimeDraft(normalizedDailyDigestReportTime);
    setSavedAllClientsReportDraft(normalizedAllClientsReportDraft);
    setAllClientsReportDraft(normalizedAllClientsReportDraft);
    return {
      workingHoursPerDay: normalizedWorkingHours,
      dailyDigestReportTime: normalizedDailyDigestReportTime,
      allClientsReportDraft: normalizedAllClientsReportDraft,
    };
  }, []);

  const refreshDispatchSettings = useCallback(async () => {
    try {
      const response = await getDispatchControlBoardSettings();
      applyDispatchSettingsState(response?.data);
      return response?.data || {};
    } catch (error) {
      console.error('Failed to load dispatch KPI settings:', error);
      return null;
    }
  }, [applyDispatchSettingsState]);

  useEffect(() => {
    let mounted = true;
    getKpiClients()
      .then((response) => {
        if (!mounted) return;
        if (response?.success && Array.isArray(response.clients)) {
          setClientList(normalizeClientOptions(response.clients));
        }
      })
      .catch(() => {
        if (mounted) setClientList(normalizeClientOptions(clients));
      });

    return () => {
      mounted = false;
    };
  }, [clients]);

  useEffect(() => {
    refreshDispatchSettings();
  }, [refreshDispatchSettings]);

  useEffect(() => {
    if ((lens === 'selected-day' || lens === 'yesterday') && showHeatmap) {
      setShowHeatmap(false);
    }
  }, [lens, showHeatmap]);

  useEffect(() => {
    if (!showActiveOperatorControls && operatorActivityFilter !== 'all') {
      setOperatorActivityFilter('all');
    }
  }, [operatorActivityFilter, showActiveOperatorControls]);

  useEffect(() => {
    const boardWorkingHours = controlBoard?.meta?.workingHoursPerDay;
    if (boardWorkingHours == null) return;

    const normalizedWorkingHours = normalizeWorkingHoursValue(boardWorkingHours);
    if (savedWorkingHoursPerDay == null) setSavedWorkingHoursPerDay(normalizedWorkingHours);
    if (!workingHoursDraft) setWorkingHoursDraft(String(normalizedWorkingHours));
    if (!appliedWorkingHours) setAppliedWorkingHours(String(normalizedWorkingHours));
  }, [appliedWorkingHours, controlBoard?.meta?.workingHoursPerDay, savedWorkingHoursPerDay, workingHoursDraft]);

  const refreshClientList = useCallback(async () => {
    const response = await getKpiClients();
    if (response?.success && Array.isArray(response.clients)) {
      setClientList(normalizeClientOptions(response.clients));
      return response.clients;
    }
    throw new Error(response?.message || 'Failed to refresh client target list.');
  }, []);

  const fetchControlBoard = useCallback(async () => {
    setBoardLoading(true);
    setBoardError('');

    try {
      const params = {
        client,
        lens,
        priority,
      };
      if (excludedClientsParam) params.excludedClients = excludedClientsParam;
      if (lens === 'selected-day') {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      if (lens === 'rolling') params.windowHours = windowHours;
      if (appliedWorkingHours) params.workingHoursPerDay = normalizeWorkingHoursValue(appliedWorkingHours);

      const response = await getDispatchControlBoard(params);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to load dispatch control board.');
      }

      setControlBoard(response.data);
      setLastUpdated(response.data?.meta?.lastUpdatedAt || new Date().toISOString());
    } catch (error) {
      console.error('Failed to load dispatch control board:', error);
      setBoardError(error?.response?.data?.message || error?.message || 'Failed to load dispatch control board.');
    } finally {
      setBoardLoading(false);
    }
  }, [appliedWorkingHours, client, endDate, excludedClientsParam, lens, priority, startDate, windowHours]);

  const fetchHeatmap = useCallback(async () => {
    if (!showHeatmap || !heatmapAvailable) {
      setHeatmapError('');
      setHeatmapData({ hours: [], categories: [] });
      return;
    }

    setHeatmapLoading(true);
    setHeatmapError('');
    try {
      const response = await getHourlyHeatmap(client, heatmapHours, excludedClientsParam);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to load hourly heatmap.');
      }
      setHeatmapData(response.data || { hours: [], categories: [] });
    } catch (error) {
      console.error('Failed to load hourly heatmap:', error);
      setHeatmapError(error?.response?.data?.message || error?.message || 'Failed to load hourly heatmap.');
      setHeatmapData({ hours: [], categories: [] });
    } finally {
      setHeatmapLoading(false);
    }
  }, [client, excludedClientsParam, heatmapAvailable, heatmapHours, showHeatmap]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchControlBoard(),
      fetchHeatmap(),
    ]);
  }, [fetchControlBoard, fetchHeatmap]);

  const handleApplyWorkingHours = useCallback(() => {
    const normalizedWorkingHours = normalizeWorkingHoursValue(workingHoursDraft, effectiveSavedWorkingHours);
    setWorkingHoursDraft(String(normalizedWorkingHours));
    setAppliedWorkingHours(String(normalizedWorkingHours));
  }, [effectiveSavedWorkingHours, workingHoursDraft]);

  const handleSaveWorkingHoursDefault = useCallback(async () => {
    const normalizedWorkingHours = normalizeWorkingHoursValue(workingHoursDraft, effectiveSavedWorkingHours);
    setWorkingHoursSaving(true);
    try {
      const response = await updateDispatchControlBoardSettings({
        workingHoursPerDay: normalizedWorkingHours,
      });
      const appliedSettings = applyDispatchSettingsState(response?.data || { workingHoursPerDay: normalizedWorkingHours });
      const savedValue = normalizeWorkingHoursValue(appliedSettings?.workingHoursPerDay, normalizedWorkingHours);
      showSnackbar('Working day hours default saved.', 'success');
      await refreshAll();
    } catch (error) {
      console.error('Failed to save working day hours default:', error);
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to save working day hours.', 'error');
    } finally {
      setWorkingHoursSaving(false);
    }
  }, [applyDispatchSettingsState, effectiveSavedWorkingHours, refreshAll, showSnackbar, workingHoursDraft]);

  const openTargetManager = useCallback(async () => {
    if (!canManageClients) return;
    setTargetManagerOpen(true);
    setTargetManagerLoading(true);
    setTargetManagerSaving(false);
    setTargetManagerError('');
    setTargetManagerSearch('');
    setTargetManagerListFilter('all');

    try {
      const rows = await getClients();
      const normalizedRows = Array.isArray(rows) ? rows : [];
      setTargetManagerClients(normalizedRows);
      setTargetDrafts(buildTargetDraftMap(normalizedRows));
      setSelectedTargetClientId(normalizedRows[0]?.clientId ?? null);
    } catch (error) {
      console.error('Failed to load clients for target manager:', error);
      setTargetManagerClients([]);
      setTargetDrafts({});
      setSelectedTargetClientId(null);
      setTargetManagerError(error?.response?.data?.message || error?.message || 'Failed to load client targets.');
    } finally {
      setTargetManagerLoading(false);
    }
  }, [canManageClients]);

  const closeTargetManager = useCallback(() => {
    if (targetManagerSaving) return;
    setTargetManagerOpen(false);
    setTargetManagerLoading(false);
    setTargetManagerSaving(false);
    setTargetManagerError('');
    setTargetManagerSearch('');
    setTargetManagerListFilter('all');
    setTargetManagerClients([]);
    setTargetDrafts({});
    setSelectedTargetClientId(null);
  }, [targetManagerSaving]);

  const handleTargetDraftChange = useCallback((clientId, nextValue) => {
    setTargetDrafts((current) => ({
      ...current,
      [clientId]: sanitizeDispatchTargetInput(nextValue),
    }));
  }, []);

  const changedTargetRows = useMemo(() => (
    targetManagerClients.filter((row) => {
      const rawValue = targetDrafts[row.clientId];
      if (rawValue == null || rawValue === '') return false;
      const parsed = Number.parseInt(rawValue, 10);
      if (!Number.isFinite(parsed)) return false;
      return parsed !== Number(row.dispatchTargetPerHour ?? DEFAULT_OPERATOR_TARGET);
    })
  ), [targetDrafts, targetManagerClients]);

  const invalidTargetClientIds = useMemo(() => {
    const invalidIds = new Set();
    targetManagerClients.forEach((row) => {
      const rawValue = targetDrafts[row.clientId];
      if (rawValue == null || rawValue === '') {
        invalidIds.add(row.clientId);
        return;
      }

      const parsed = Number.parseInt(rawValue, 10);
      if (!Number.isFinite(parsed) || parsed < MIN_DISPATCH_TARGET || parsed > MAX_DISPATCH_TARGET) {
        invalidIds.add(row.clientId);
      }
    });
    return invalidIds;
  }, [targetDrafts, targetManagerClients]);
  const targetManagerClientRows = useMemo(() => (
    targetManagerClients.map((row) => {
      const rawValue = targetDrafts[row.clientId] ?? '';
      const parsed = Number.parseInt(rawValue, 10);
      const currentTarget = Number(row.dispatchTargetPerHour ?? DEFAULT_OPERATOR_TARGET);
      const isDirty = Number.isFinite(parsed) && parsed !== currentTarget;
      const hasError = invalidTargetClientIds.has(row.clientId);

      return {
        ...row,
        rawValue,
        parsed,
        currentTarget,
        isDirty,
        hasError,
      };
    })
  ), [invalidTargetClientIds, targetDrafts, targetManagerClients]);
  const filteredTargetManagerClients = useMemo(() => {
    const query = targetManagerSearch.trim().toLowerCase();
    let filteredRows = query
      ? targetManagerClientRows.filter((row) => String(row.clientName || '').toLowerCase().includes(query))
      : targetManagerClientRows;

    if (targetManagerListFilter === 'changed') {
      filteredRows = filteredRows.filter((row) => row.isDirty);
    } else if (targetManagerListFilter === 'attention') {
      filteredRows = filteredRows.filter((row) => row.hasError);
    }

    return [...filteredRows].sort((left, right) => {
      if (left.hasError !== right.hasError) return left.hasError ? -1 : 1;
      if (left.isDirty !== right.isDirty) return left.isDirty ? -1 : 1;
      return String(left.clientName || '').localeCompare(String(right.clientName || ''));
    });
  }, [targetManagerClientRows, targetManagerListFilter, targetManagerSearch]);
  const targetManagerErrorCount = useMemo(
    () => targetManagerClientRows.filter((row) => row.hasError).length,
    [targetManagerClientRows]
  );
  const selectedTargetClientRow = useMemo(
    () => targetManagerClientRows.find((row) => row.clientId === selectedTargetClientId) || null,
    [selectedTargetClientId, targetManagerClientRows]
  );
  const selectedTargetDraftValue = useMemo(() => {
    if (!selectedTargetClientRow) return null;
    return Number.isFinite(selectedTargetClientRow.parsed) ? selectedTargetClientRow.parsed : null;
  }, [selectedTargetClientRow]);
  const selectedTargetDelta = useMemo(() => {
    if (!selectedTargetClientRow || selectedTargetDraftValue == null) return null;
    return selectedTargetDraftValue - selectedTargetClientRow.currentTarget;
  }, [selectedTargetClientRow, selectedTargetDraftValue]);

  useEffect(() => {
    if (!targetManagerOpen) return;
    if (selectedTargetClientId == null) {
      setSelectedTargetClientId(filteredTargetManagerClients[0]?.clientId ?? null);
      return;
    }

    const selectedRowIsVisible = filteredTargetManagerClients.some(
      (row) => row.clientId === selectedTargetClientId
    );

    if (!selectedRowIsVisible) {
      setSelectedTargetClientId(filteredTargetManagerClients[0]?.clientId ?? null);
    }
  }, [filteredTargetManagerClients, selectedTargetClientId, targetManagerOpen]);

  const handleSaveTargetManager = useCallback(async () => {
    if (!changedTargetRows.length) {
      closeTargetManager();
      return;
    }

    if (invalidTargetClientIds.size > 0) {
      setTargetManagerError(`Targets must be between ${MIN_DISPATCH_TARGET} and ${MAX_DISPATCH_TARGET} per hour.`);
      showSnackbar(`Targets must be between ${MIN_DISPATCH_TARGET} and ${MAX_DISPATCH_TARGET} per hour.`, 'warning');
      return;
    }

    setTargetManagerSaving(true);
    setTargetManagerError('');

    try {
      for (const row of changedTargetRows) {
        const parsedTarget = Number.parseInt(targetDrafts[row.clientId], 10);
        const dispatchTargetPerHour = Math.min(Math.max(parsedTarget, MIN_DISPATCH_TARGET), MAX_DISPATCH_TARGET);
        await editClient(row.clientId, {
          clientName: row.clientName,
          fulfilmentChannel: row.fulfilmentChannel,
          dispatchTargetPerHour,
        });
      }

      await refreshClientList();
      await refreshAll();
      showSnackbar(`Saved ${changedTargetRows.length} client target${changedTargetRows.length === 1 ? '' : 's'}.`, 'success');
      closeTargetManager();
    } catch (error) {
      console.error('Failed to save KPI target manager changes:', error);
      setTargetManagerError(error?.response?.data?.message || error?.message || 'Failed to save client targets.');
      showSnackbar('Failed to save client targets.', 'error');
    } finally {
      setTargetManagerSaving(false);
    }
  }, [changedTargetRows, closeTargetManager, invalidTargetClientIds.size, refreshAll, refreshClientList, showSnackbar, targetDrafts]);

  const openReportManager = useCallback(async () => {
    if (!canManageClients) return;
    setReportManagerOpen(true);
    setReportManagerLoading(true);
    setReportManagerSaving(false);
    setReportManagerError('');
    setReportManagerSearch('');
    setReportManagerListFilter('all');
    setSelectedReportConfigId(ALL_CLIENTS_REPORT_KEY);
    setReportPreviewDate(
      lens === 'selected-day'
        ? (startDate || endDate || getLocalDateInputValue())
        : lens === 'yesterday'
          ? (controlBoard?.meta?.startDate || getYesterdayDateInputValue())
          : getLocalDateInputValue()
    );

    try {
      const rows = await getClients();
      const normalizedRows = Array.isArray(rows) ? rows : [];
      setReportManagerClients(normalizedRows);
      setReportScheduleDrafts(buildReportScheduleDraftMap(normalizedRows));
      await refreshDispatchSettings();
    } catch (error) {
      console.error('Failed to load clients for report schedule manager:', error);
      setReportManagerClients([]);
      setReportScheduleDrafts({});
      setReportManagerError(error?.response?.data?.message || error?.message || 'Failed to load report schedules.');
    } finally {
      setReportManagerLoading(false);
    }
  }, [canManageClients, controlBoard?.meta?.startDate, endDate, lens, refreshDispatchSettings, startDate]);

  const closeReportManager = useCallback(() => {
    if (reportManagerSaving || reportTestSendingClientId || reportTestDownloadingClientId) return;
    setReportManagerOpen(false);
    setReportManagerLoading(false);
    setReportManagerSaving(false);
    setReportManagerError('');
    setReportManagerSearch('');
    setReportManagerListFilter('all');
    setReportManagerClients([]);
    setReportScheduleDrafts({});
    setSelectedReportConfigId(ALL_CLIENTS_REPORT_KEY);
    setDailyDigestReportTimeDraft(savedDailyDigestReportTime);
    setAllClientsReportDraft(savedAllClientsReportDraft);
    setReportTestSendingClientId(null);
    setReportTestDownloadingClientId(null);
  }, [reportManagerSaving, reportTestDownloadingClientId, reportTestSendingClientId, savedAllClientsReportDraft, savedDailyDigestReportTime]);

  const handleReportScheduleEnabledChange = useCallback((clientId, enabled) => {
    setReportScheduleDrafts((current) => ({
      ...current,
      [clientId]: {
        ...(current[clientId] || { recipients: '', time: DEFAULT_DAILY_KPI_REPORT_TIME }),
        enabled,
      },
    }));
  }, []);

  const handleReportRecipientsChange = useCallback((clientId, recipients) => {
    setReportScheduleDrafts((current) => ({
      ...current,
      [clientId]: {
        ...(current[clientId] || { enabled: false, time: DEFAULT_DAILY_KPI_REPORT_TIME }),
        recipients,
      },
    }));
  }, []);

  const handleReportTimeChange = useCallback((clientId, time) => {
    setReportScheduleDrafts((current) => ({
      ...current,
      [clientId]: {
        ...(current[clientId] || { enabled: false, recipients: '' }),
        time,
      },
    }));
  }, []);

  const handleAllClientsReportDraftChange = useCallback((field, value) => {
    setAllClientsReportDraft((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);
  const isDailyDigestReportTimeDirty = useMemo(
    () => normalizeReportTimeValue(dailyDigestReportTimeDraft) !== normalizeReportTimeValue(savedDailyDigestReportTime),
    [dailyDigestReportTimeDraft, savedDailyDigestReportTime]
  );
  const hasDailyDigestReportTimeError = useMemo(
    () => !isValidReportTimeValue(dailyDigestReportTimeDraft),
    [dailyDigestReportTimeDraft]
  );

  const invalidReportClientIds = useMemo(() => {
    const invalidIds = new Set();
    reportManagerClients.forEach((row) => {
      const draft = reportScheduleDrafts[row.clientId];
      if (!draft) return;
      if (getInvalidReportRecipients(draft.recipients).length > 0) {
        invalidIds.add(row.clientId);
      }
    });
    return invalidIds;
  }, [reportManagerClients, reportScheduleDrafts]);

  const invalidReportTimeClientIds = useMemo(() => {
    const invalidIds = new Set();
    reportManagerClients.forEach((row) => {
      const draft = reportScheduleDrafts[row.clientId];
      if (!draft) return;
      if (!isValidReportTimeValue(draft.time)) {
        invalidIds.add(row.clientId);
      }
    });
    return invalidIds;
  }, [reportManagerClients, reportScheduleDrafts]);

  const missingReportRecipientClientIds = useMemo(() => {
    const missingIds = new Set();
    reportManagerClients.forEach((row) => {
      const draft = reportScheduleDrafts[row.clientId];
      if (!draft?.enabled) return;
      if (!parseReportRecipientList(draft.recipients).length) {
        missingIds.add(row.clientId);
      }
    });
    return missingIds;
  }, [reportManagerClients, reportScheduleDrafts]);

  const isAllClientsReportDirty = useMemo(
    () => !areReportScheduleDraftsEqual(allClientsReportDraft, savedAllClientsReportDraft),
    [allClientsReportDraft, savedAllClientsReportDraft]
  );
  const allClientsReportRecipients = useMemo(
    () => parseReportRecipientList(allClientsReportDraft.recipients),
    [allClientsReportDraft.recipients]
  );
  const allClientsReportInvalidRecipients = useMemo(
    () => getInvalidReportRecipients(allClientsReportDraft.recipients),
    [allClientsReportDraft.recipients]
  );
  const allClientsReportHasInvalidTime = useMemo(
    () => !isValidReportTimeValue(allClientsReportDraft.time),
    [allClientsReportDraft.time]
  );
  const allClientsReportHasMissingRecipients = useMemo(
    () => Boolean(allClientsReportDraft.enabled) && !allClientsReportRecipients.length,
    [allClientsReportDraft.enabled, allClientsReportRecipients.length]
  );
  const reportManagerClientRows = useMemo(() => (
    reportManagerClients.map((row) => {
      const draft = reportScheduleDrafts[row.clientId] || buildClientReportScheduleDraft(row);
      const normalizedRecipients = parseReportRecipientList(draft.recipients);
      const invalidRecipients = getInvalidReportRecipients(draft.recipients);
      const hasInvalidTime = invalidReportTimeClientIds.has(row.clientId);
      const hasMissingRecipients = missingReportRecipientClientIds.has(row.clientId);
      const isDirty = (
        Boolean(draft.enabled) !== Boolean(row.dailyKpiReportEnabled)
        || normalizeReportRecipientsValue(draft.recipients) !== normalizeReportRecipientsValue(row.dailyKpiReportRecipients)
        || normalizeReportTimeValue(draft.time) !== normalizeReportTimeValue(row.dailyKpiReportTime)
      );
      const hasError = invalidReportClientIds.has(row.clientId) || hasMissingRecipients || hasInvalidTime;

      return {
        ...row,
        draft,
        normalizedRecipients,
        invalidRecipients,
        hasInvalidTime,
        hasMissingRecipients,
        isDirty,
        hasError,
      };
    })
  ), [
    invalidReportClientIds,
    invalidReportTimeClientIds,
    missingReportRecipientClientIds,
    reportManagerClients,
    reportScheduleDrafts,
  ]);
  const filteredReportManagerClients = useMemo(() => {
    const query = reportManagerSearch.trim().toLowerCase();
    let filteredRows = query
      ? reportManagerClientRows.filter((row) => String(row.clientName || '').toLowerCase().includes(query))
      : reportManagerClientRows;

    if (reportManagerListFilter === 'enabled') {
      filteredRows = filteredRows.filter((row) => Boolean(row.draft.enabled));
    } else if (reportManagerListFilter === 'attention') {
      filteredRows = filteredRows.filter((row) => row.hasError);
    }

    return [...filteredRows].sort((left, right) => {
      const leftEnabled = Boolean(left.draft.enabled);
      const rightEnabled = Boolean(right.draft.enabled);
      if (leftEnabled !== rightEnabled) return leftEnabled ? -1 : 1;
      return String(left.clientName || '').localeCompare(String(right.clientName || ''));
    });
  }, [reportManagerClientRows, reportManagerListFilter, reportManagerSearch]);
  const changedReportRows = useMemo(
    () => reportManagerClientRows.filter((row) => row.isDirty),
    [reportManagerClientRows]
  );
  const enabledReportManagerClientCount = useMemo(
    () => reportManagerClientRows.filter((row) => Boolean(row.draft.enabled)).length,
    [reportManagerClientRows]
  );
  const reportManagerClientErrorCount = useMemo(
    () => reportManagerClientRows.filter((row) => row.hasError).length,
    [reportManagerClientRows]
  );
  const allClientsReportRow = useMemo(() => ({
    clientId: ALL_CLIENTS_REPORT_KEY,
    clientName: 'ALL',
    dailyKpiReportTime: normalizeReportTimeValue(allClientsReportDraft.time),
  }), [allClientsReportDraft.time]);
  const isAllClientsReportSelected = selectedReportConfigId === ALL_CLIENTS_REPORT_KEY;
  const selectedClientReportRow = useMemo(
    () => reportManagerClientRows.find((row) => row.clientId === selectedReportConfigId) || null,
    [reportManagerClientRows, selectedReportConfigId]
  );
  const selectedReportManagerRow = isAllClientsReportSelected ? allClientsReportRow : selectedClientReportRow;
  const selectedReportDraft = isAllClientsReportSelected
    ? allClientsReportDraft
    : (selectedClientReportRow?.draft || buildClientReportScheduleDraft(selectedClientReportRow || {}));
  const selectedReportRecipients = isAllClientsReportSelected
    ? allClientsReportRecipients
    : (selectedClientReportRow?.normalizedRecipients || []);
  const selectedReportInvalidRecipients = isAllClientsReportSelected
    ? allClientsReportInvalidRecipients
    : (selectedClientReportRow?.invalidRecipients || []);
  const selectedReportHasInvalidTime = isAllClientsReportSelected
    ? allClientsReportHasInvalidTime
    : Boolean(selectedClientReportRow?.hasInvalidTime);
  const selectedReportHasMissingRecipients = isAllClientsReportSelected
    ? allClientsReportHasMissingRecipients
    : Boolean(selectedClientReportRow?.hasMissingRecipients);
  const selectedReportHasError = isAllClientsReportSelected
    ? (allClientsReportInvalidRecipients.length > 0 || allClientsReportHasMissingRecipients || allClientsReportHasInvalidTime)
    : Boolean(selectedClientReportRow?.hasError);
  const selectedReportIsDirty = isAllClientsReportSelected
    ? isAllClientsReportDirty
    : Boolean(selectedClientReportRow?.isDirty);
  const selectedReportTitle = isAllClientsReportSelected
    ? 'All Clients Overall Report'
    : (selectedClientReportRow?.clientName || 'Client report schedule');
  const selectedReportDescription = isAllClientsReportSelected
    ? 'Recipients listed here receive the Overall PDF inside their single daily digest email.'
    : `Recipients listed here receive the ${selectedClientReportRow?.clientName || 'selected client'} PDF inside their single daily digest email.`;
  const formattedReportPreviewDate = useMemo(
    () => formatReportPreviewDateValue(reportPreviewDate),
    [reportPreviewDate]
  );
  const totalDirtyReportChanges = changedReportRows.length + (isAllClientsReportDirty ? 1 : 0) + (isDailyDigestReportTimeDirty ? 1 : 0);
  const getReportDraftForRow = useCallback((row) => {
    if (row?.clientId === ALL_CLIENTS_REPORT_KEY) {
      return allClientsReportDraft;
    }
    return reportScheduleDrafts[row.clientId] || buildClientReportScheduleDraft(row);
  }, [allClientsReportDraft, reportScheduleDrafts]);

  useEffect(() => {
    if (!reportManagerOpen) return;
    if (selectedReportConfigId === ALL_CLIENTS_REPORT_KEY) return;

    const selectedRowIsVisible = filteredReportManagerClients.some(
      (row) => row.clientId === selectedReportConfigId
    );

    if (!selectedRowIsVisible) {
      setSelectedReportConfigId(filteredReportManagerClients[0]?.clientId || ALL_CLIENTS_REPORT_KEY);
    }
  }, [filteredReportManagerClients, reportManagerOpen, selectedReportConfigId]);

  const handleSaveReportManager = useCallback(async () => {
    const totalPendingChanges = changedReportRows.length + (isAllClientsReportDirty ? 1 : 0) + (isDailyDigestReportTimeDirty ? 1 : 0);
    if (!totalPendingChanges) {
      closeReportManager();
      return;
    }

    if (hasDailyDigestReportTimeError) {
      setReportManagerError('Daily digest send time is invalid. Use HH:mm format.');
      showSnackbar('Daily digest send time is invalid. Use HH:mm format.', 'warning');
      return;
    }

    if (invalidReportClientIds.size > 0) {
      setReportManagerError('One or more recipient lists include invalid email addresses.');
      showSnackbar('One or more recipient lists include invalid email addresses.', 'warning');
      return;
    }

    if (missingReportRecipientClientIds.size > 0) {
      setReportManagerError('Enabled schedules must include at least one recipient email.');
      showSnackbar('Enabled schedules must include at least one recipient email.', 'warning');
      return;
    }

    if (invalidReportTimeClientIds.size > 0) {
      setReportManagerError('One or more daily send times are invalid. Use HH:mm format.');
      showSnackbar('One or more daily send times are invalid. Use HH:mm format.', 'warning');
      return;
    }

    if (allClientsReportInvalidRecipients.length > 0) {
      const invalidMessage = `Invalid All Clients report email(s): ${allClientsReportInvalidRecipients.join(', ')}`;
      setReportManagerError(invalidMessage);
      showSnackbar(invalidMessage, 'warning');
      return;
    }

    if (allClientsReportHasMissingRecipients) {
      const missingRecipientsMessage = 'At least one email is required when the All Clients report schedule is enabled.';
      setReportManagerError(missingRecipientsMessage);
      showSnackbar(missingRecipientsMessage, 'warning');
      return;
    }

    if (allClientsReportHasInvalidTime) {
      const invalidTimeMessage = 'All Clients report time must use HH:mm format.';
      setReportManagerError(invalidTimeMessage);
      showSnackbar(invalidTimeMessage, 'warning');
      return;
    }

    setReportManagerSaving(true);
    setReportManagerError('');

    let savedDailyDigestTimeSetting = false;
    let savedAllClientsReport = false;
    let savedClientSchedules = 0;
    const failureMessages = [];

    try {
      if (isDailyDigestReportTimeDirty) {
        try {
          const response = await updateDispatchControlBoardSettings({
            dailyDigestReportTime: normalizeReportTimeValue(dailyDigestReportTimeDraft),
          });
          applyDispatchSettingsState(response?.data);
          savedDailyDigestTimeSetting = true;
        } catch (error) {
          console.error('Failed to save daily digest send time:', error);
          failureMessages.push(error?.response?.data?.message || error?.message || 'Failed to save the daily digest send time.');
        }
      }

      if (isAllClientsReportDirty) {
        try {
          const response = await updateDispatchControlBoardSettings({
            allClientsReportEnabled: Boolean(allClientsReportDraft.enabled),
            allClientsReportRecipients: allClientsReportDraft.recipients,
            allClientsReportTime: normalizeReportTimeValue(allClientsReportDraft.time),
          });
          applyDispatchSettingsState(response?.data);
          savedAllClientsReport = true;
        } catch (error) {
          console.error('Failed to save All Clients report schedule:', error);
          failureMessages.push(error?.response?.data?.message || error?.message || 'Failed to save the All Clients report schedule.');
        }
      }

      if (changedReportRows.length) {
        for (const row of changedReportRows) {
          const draft = reportScheduleDrafts[row.clientId] || buildClientReportScheduleDraft(row);
          try {
            await editClient(row.clientId, {
              clientName: row.clientName,
              fulfilmentChannel: row.fulfilmentChannel,
              dispatchTargetPerHour: row.dispatchTargetPerHour,
              dailyKpiReportEnabled: draft.enabled,
              dailyKpiReportRecipients: draft.recipients,
              dailyKpiReportTime: normalizeReportTimeValue(draft.time),
            });
            savedClientSchedules += 1;
          } catch (error) {
            console.error('Failed to save KPI report schedule change:', error);
            failureMessages.push(
              error?.response?.data?.message
              || error?.message
              || `Failed to save report schedule for ${row.clientName}.`
            );
          }
        }

        if (savedClientSchedules > 0) {
          const rows = await getClients();
          const normalizedRows = Array.isArray(rows) ? rows : [];
          setReportManagerClients(normalizedRows);
          setReportScheduleDrafts(buildReportScheduleDraftMap(normalizedRows));
        }
      }

      if (failureMessages.length) {
        const failureSummary = failureMessages.join(' ');
        setReportManagerError(failureSummary);
        if (savedDailyDigestTimeSetting || savedAllClientsReport || savedClientSchedules > 0) {
          const successParts = [];
          if (savedDailyDigestTimeSetting) successParts.push('daily digest send time');
          if (savedAllClientsReport) successParts.push('All Clients report schedule');
          if (savedClientSchedules > 0) {
            successParts.push(`${formatNumber(savedClientSchedules)} client report schedule${savedClientSchedules === 1 ? '' : 's'}`);
          }
          showSnackbar(`Saved ${successParts.join(' and ')}, but ${failureSummary}`, 'warning');
        } else {
          showSnackbar(failureSummary, 'error');
        }
        return;
      }

      const successParts = [];
      if (savedDailyDigestTimeSetting) successParts.push('daily digest send time');
      if (savedAllClientsReport) successParts.push('All Clients report schedule');
      if (savedClientSchedules > 0) {
        successParts.push(`${formatNumber(savedClientSchedules)} client report schedule${savedClientSchedules === 1 ? '' : 's'}`);
      }
      showSnackbar(`Saved ${successParts.join(' and ')}.`, 'success');
      closeReportManager();
    } finally {
      setReportManagerSaving(false);
    }
  }, [
    allClientsReportDraft,
    allClientsReportHasInvalidTime,
    allClientsReportHasMissingRecipients,
    allClientsReportInvalidRecipients,
    applyDispatchSettingsState,
    changedReportRows,
    closeReportManager,
    dailyDigestReportTimeDraft,
    hasDailyDigestReportTimeError,
    invalidReportClientIds.size,
    invalidReportTimeClientIds.size,
    isDailyDigestReportTimeDirty,
    missingReportRecipientClientIds.size,
    reportScheduleDrafts,
    savedAllClientsReportDraft,
    showSnackbar,
  ]);

  const handleSendTestReport = useCallback(async (row) => {
    if (!canManageUsers) return;
    if (!reportPreviewDate) {
      showSnackbar('Choose a preview date before sending a test report.', 'warning');
      return;
    }

    const draft = getReportDraftForRow(row);
    setReportTestSendingClientId(row.clientId);
    try {
      const reportExcludedClientsParam = row.clientName === 'ALL' ? excludedClientsParam : '';
      const response = await sendTestDispatchKpiReport({
        clientName: row.clientName,
        reportDate: reportPreviewDate,
        scheduleTime: normalizeReportTimeValue(draft.time || row.dailyKpiReportTime),
        ...(reportExcludedClientsParam ? { excludedClients: reportExcludedClientsParam } : {}),
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to send test KPI PDF report.');
      }

      showSnackbar(response.message || `Test KPI PDF sent to ${TEST_KPI_REPORT_EMAIL}.`, 'success');
    } catch (error) {
      console.error('Failed to send test KPI PDF report:', error);
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to send test KPI PDF report.', 'error');
    } finally {
      setReportTestSendingClientId(null);
    }
  }, [canManageUsers, excludedClientsParam, getReportDraftForRow, reportPreviewDate, showSnackbar]);

  const handleDownloadTestReport = useCallback(async (row) => {
    if (!reportPreviewDate) {
      showSnackbar('Choose a preview date before downloading the test PDF.', 'warning');
      return;
    }

    const draft = getReportDraftForRow(row);
    setReportTestDownloadingClientId(row.clientId);
    try {
      const reportExcludedClientsParam = row.clientName === 'ALL' ? excludedClientsParam : '';
      const response = await downloadTestDispatchKpiReport({
        clientName: row.clientName,
        reportDate: reportPreviewDate,
        scheduleTime: normalizeReportTimeValue(draft.time || row.dailyKpiReportTime),
        ...(reportExcludedClientsParam ? { excludedClients: reportExcludedClientsParam } : {}),
      });

      const contentDisposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const safeClientName = String(row.clientName || 'client').replace(/[^a-zA-Z0-9-_]+/g, '_');
      const downloadName = filenameMatch?.[1] || `dispatch_kpi_daily_${reportPreviewDate}_${safeClientName}.pdf`;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      showSnackbar('Test KPI PDF downloaded.', 'success');
    } catch (error) {
      console.error('Failed to download test KPI PDF report:', error);
      let downloadErrorMessage = error?.message || 'Failed to download test KPI PDF report.';
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          downloadErrorMessage = parsed?.message || downloadErrorMessage;
        } catch {
          downloadErrorMessage = downloadErrorMessage || 'Failed to download test KPI PDF report.';
        }
      }
      showSnackbar(downloadErrorMessage, 'error');
    } finally {
      setReportTestDownloadingClientId(null);
    }
  }, [excludedClientsParam, getReportDraftForRow, reportPreviewDate, showSnackbar]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timerId = setInterval(() => {
      refreshAll();
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timerId);
  }, [autoRefresh, refreshAll]);

  const operatorRows = controlBoard?.operatorRows || [];
  const summary = controlBoard?.summary || {};
  const hourlyPace = controlBoard?.hourlyPace || [];
  const clientRisk = controlBoard?.clientRisk || [];
  const operatorSlaMisses = controlBoard?.operatorSlaMisses || [];
  const exceptions = controlBoard?.exceptions?.rows || [];
  const havingIssueSummary = controlBoard?.havingIssue?.summary || {
    totalCount: 0,
    onHoldCount: 0,
    inProgressCount: 0,
    urgentCount: 0,
  };
  const havingIssueRows = controlBoard?.havingIssue?.rows || [];
  const visibleHavingIssueRows = havingIssueRows.slice(0, 50);
  const boardWorkingHoursPerDay = controlBoard?.meta?.workingHoursPerDay ?? effectiveSavedWorkingHours;

  const operatorActivityCounts = useMemo(() => ({
    active: operatorRows.filter((row) => Boolean(row.isActiveNow)).length,
    earlier: operatorRows.filter((row) => row.isActiveNow === false).length,
  }), [operatorRows]);

  const scopedOperatorRows = useMemo(() => {
    if (!showActiveOperatorControls || operatorActivityFilter === 'all') return operatorRows;
    if (operatorActivityFilter === 'active') return operatorRows.filter((row) => Boolean(row.isActiveNow));
    if (operatorActivityFilter === 'earlier') return operatorRows.filter((row) => row.isActiveNow === false);
    return operatorRows;
  }, [operatorActivityFilter, operatorRows, showActiveOperatorControls]);

  const filteredOperatorRows = useMemo(() => {
    const query = operatorQuery.trim().toLowerCase();
    if (!query) return scopedOperatorRows;
    return scopedOperatorRows.filter((row) => (row.operator || '').toLowerCase().includes(query));
  }, [operatorQuery, scopedOperatorRows]);

  const sortedOperatorRows = useMemo(() => {
    if (operatorSortBy === 'intervention') return filteredOperatorRows;

    return [...filteredOperatorRows].sort((left, right) => {
      let leftValue;
      let rightValue;
      switch (operatorSortBy) {
        case 'operator':
          leftValue = left.operator || '';
          rightValue = right.operator || '';
          break;
        case 'currentHourOrders':
          leftValue = left.currentHourOrders;
          rightValue = right.currentHourOrders;
          break;
        case 'currentHourTargetPct':
          leftValue = left.currentHourTargetPct;
          rightValue = right.currentHourTargetPct;
          break;
        case 'totalOrders':
          leftValue = left.totalOrders;
          rightValue = right.totalOrders;
          break;
        case 'avgOrdersPerHour':
          leftValue = left.avgOrdersPerHour;
          rightValue = right.avgOrdersPerHour;
          break;
        case 'targetPct':
          leftValue = left.targetPct;
          rightValue = right.targetPct;
          break;
        case 'slaOnTimePct':
          leftValue = left.slaOnTimePct;
          rightValue = right.slaOnTimePct;
          break;
        case 'lastActivity':
          leftValue = left.lastActivity ? new Date(left.lastActivity).getTime() : null;
          rightValue = right.lastActivity ? new Date(right.lastActivity).getTime() : null;
          break;
        default:
          leftValue = left.totalOrders;
          rightValue = right.totalOrders;
      }
      return compareSortValues(leftValue, rightValue, operatorSortDir);
    });
  }, [filteredOperatorRows, operatorSortBy, operatorSortDir]);

  const selectedOperatorMissRows = useMemo(() => {
    if (!selectedOperatorDrilldown?.operator) return [];
    return operatorSlaMisses.filter((row) => normalizeOperatorKey(row.operator) === normalizeOperatorKey(selectedOperatorDrilldown.operator));
  }, [operatorSlaMisses, selectedOperatorDrilldown]);

  const selectedOperatorMissSummary = useMemo(() => {
    return selectedOperatorMissRows.reduce((accumulator, row) => {
      accumulator.total += 1;
      if (row.isExpress) accumulator.express += 1;
      else accumulator.standard += 1;
      return accumulator;
    }, { total: 0, express: 0, standard: 0 });
  }, [selectedOperatorMissRows]);

  const hourlyChartData = useMemo(() => {
    const distinctDays = new Set(hourlyPace.map((item) => String(item.hour || '').slice(0, 10)));
    const includeDay = distinctDays.size > 1;

    return hourlyPace.map((item) => ({
      ...item,
      label: formatHourLabel(item.hour, includeDay),
      tone: getTargetTone(item.targetPct),
    }));
  }, [hourlyPace]);

  const summaryCards = useMemo(() => {
    const operatorTargetRateLabel = formatRate(operatorTargetPerHour, 0);
    const activeIssueOrdersInQueue = Number(summary.activeIssueOrdersInOutstandingRequiredQueue || 0);
    const activeOperatorCount = Number(summary.activeOperators || 0);
    const activeOperatorLabel = `${formatNumber(summary.activeOperators)} active ${activeOperatorCount === 1 ? 'operator' : 'operators'}`;
    const totalActiveHoursLabel = `${formatNumber(summary.totalActiveOperatorHours, 1)} total operator-active hours`;
    const hoursBelowTargetValue = formatHoursWindowFraction(summary.hoursBelowTarget, boardWorkingHoursPerDay, 1);
    const overallAvgVsTargetCaption = `${formatRate(summary.avgOrdersPerHour)} overall avg vs ${operatorTargetRateLabel} target`;
    const requiredDispatchFraction = formatFraction(
      summary.requiredDispatchOrdersCompletedInScope,
      summary.requiredDispatchOrdersInScope
    );
    const remainingRequiredDispatchOrders = (
      summary.requiredDispatchOrdersInScope == null || summary.requiredDispatchOrdersCompletedInScope == null
    )
      ? null
      : Math.max(
        Number(summary.outstandingRequiredDispatchOrders ?? (
          Number(summary.requiredDispatchOrdersInScope || 0) - Number(summary.requiredDispatchOrdersCompletedInScope || 0)
        )),
        0
      );
    const workingHoursLabel = formatHours(boardWorkingHoursPerDay, 1);
    const overallProductivityHelper = client === 'ALL'
      ? `Based on ${totalActiveHoursLabel} using the default ${formatNumber(operatorTargetPerHour)}/hr target per operator`
      : `Based on ${totalActiveHoursLabel} across the current view`;
    const currentTargetCaption = `${activeOperatorLabel} x ${formatNumber(operatorTargetPerHour)}/hr`;
    const currentHourRateVsTargetPct = hasTarget
      && Number(summary.teamCurrentHourRunRate || 0) > 0
      && Number(summary.teamTargetRunRate || 0) > 0
      ? Number(summary.teamCurrentHourRunRate) / Number(summary.teamTargetRunRate)
      : summary.teamPacePct;
    const queueIssueHelper = activeIssueOrdersInQueue > 0 && remainingRequiredDispatchOrders != null
      ? `${formatNumber(activeIssueOrdersInQueue)} of the ${formatNumber(remainingRequiredDispatchOrders)} remaining orders currently have active issues`
      : null;
    const queueIssueValueAdornment = activeIssueOrdersInQueue > 0 ? (
      <Chip
        size="small"
        label={`${formatNumber(activeIssueOrdersInQueue)} issue${activeIssueOrdersInQueue === 1 ? '' : 's'}`}
        sx={{
          height: { xs: 20, xl: 18 },
          alignSelf: 'center',
          borderRadius: 999,
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.warning.main, 0.26),
          backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.1),
          color: (theme) => theme.palette.warning.dark,
          '& .MuiChip-label': {
            px: 0.8,
            fontSize: { xs: 10.5, xl: 10 },
            fontWeight: 700,
            letterSpacing: 0.1,
          },
        }}
      />
    ) : null;

    if (livePacing) {
      const liveCards = [
        {
          title: 'Orders Dispatched',
          value: requiredDispatchFraction,
          caption: lens === 'today'
            ? 'Today dispatched vs current required queue'
            : 'Required-dispatch orders completed in the rolling view',
          helper: lens === 'today'
            ? `${formatNumber(summary.outstandingRequiredDispatchOrders)} still required in the live queue, including active issue orders • ${formatNumber(summary.ordersDispatched)} dispatched today`
            : `${formatNumber(summary.ordersDispatched)} total dispatches recorded in the current view`,
          icon: <LocalShippingIcon fontSize="small" />,
          tone: 'neutral',
        },
        {
          title: 'Required Queue Remaining',
          value: formatNumber(remainingRequiredDispatchOrders),
          valueAdornment: queueIssueValueAdornment,
          caption: 'Orders still left today',
          helper: activeIssueOrdersInQueue > 0
            ? `${requiredDispatchFraction} completed in the current required queue • ${queueIssueHelper}`
            : `${requiredDispatchFraction} completed in the current required queue`,
          icon: <LocalShippingIcon fontSize="small" />,
          tone: Number(remainingRequiredDispatchOrders || 0) > 0 ? 'warning' : 'success',
        },
        {
          title: 'Current Hour Pace',
          value: formatRate(summary.teamCurrentHourRunRate),
          caption: 'Projected full-hour pace if this rate continues',
          helper: `${formatNumber(summary.teamCurrentHourOrders)} dispatched so far this hour • ${formatNumber(summary.teamExpectedByNow, 1)} expected by now against the ${formatRate(summary.teamTargetRunRate)} team target`,
          icon: <SpeedIcon fontSize="small" />,
          tone: hasTarget ? getTargetTone(currentHourRateVsTargetPct) : 'neutral',
        },
        {
          title: 'Current Hour Team Target',
          value: formatRate(summary.teamTargetRunRate),
          caption: currentTargetCaption,
          helper: client === 'ALL' ? 'Combined current-hour target using the default 20/hour target in combined view' : 'Combined current-hour target for operators active now',
          icon: <QueryStatsIcon fontSize="small" />,
          tone: 'neutral',
        },
        {
          title: 'Operators Below Overall Target',
          value: formatNumber(summary.operatorsBehindPace),
          caption: 'Below 100% on overall session-based productivity',
          helper: 'Uses total dispatches divided by sessionized active hours, not just the current hour',
          icon: <WarningAmberIcon fontSize="small" />,
          tone: hasTarget && Number(summary.operatorsBehindPace || 0) > 0 ? 'warning' : 'success',
        },
        {
          title: 'Overall Productivity %',
          value: formatTargetPercent(summary.teamTargetPct),
          caption: overallAvgVsTargetCaption,
          helper: overallProductivityHelper,
          icon: <SpeedIcon fontSize="small" />,
          tone: hasTarget ? getTargetTone(summary.teamTargetPct) : 'neutral',
        },
        {
          title: `SLA Attainment So Far${priorityScopeSuffix}`,
          value: formatPercent(summary.onTimePct),
          caption: 'Current on-time share of eligible orders in this view',
          helper: 'Undispatched eligible orders count against this until they are completed on time',
          icon: <CheckCircleOutlineIcon fontSize="small" />,
          tone: Number(summary.onTimePct || 0) >= 98
            ? 'success'
            : Number(summary.onTimePct || 0) >= 95
              ? 'warning'
              : 'error',
        },
        {
          title: `Late / At Risk${priorityScopeSuffix}`,
          value: formatNumber(Number(summary.lateCount || 0) + Number(summary.atRiskCount || 0)),
          caption: `${formatNumber(summary.lateCount)} late • ${formatNumber(summary.atRiskCount)} at risk`,
          helper: 'Late = already missed SLA; at risk = due soon but not yet late',
          icon: <ScheduleIcon fontSize="small" />,
          tone: Number(summary.lateCount || 0) + Number(summary.atRiskCount || 0) > 0 ? 'error' : 'success',
        },
      ];

      return liveCards;
    }

    const historicalCards = [
      {
        title: 'Orders Dispatched',
        value: requiredDispatchFraction,
        caption: lens === 'yesterday'
          ? 'Dispatched yesterday vs current required queue'
          : 'Dispatched in this selected range vs current required queue',
        helper: `${formatNumber(summary.outstandingRequiredDispatchOrders)} still required in the current queue, including active issue orders • ${formatNumber(summary.ordersDispatched)} dispatched in this view`,
        icon: <LocalShippingIcon fontSize="small" />,
        tone: 'neutral',
      },
      {
        title: 'Required Queue Remaining',
        value: formatNumber(remainingRequiredDispatchOrders),
        valueAdornment: queueIssueValueAdornment,
        caption: lens === 'yesterday'
          ? 'Orders still left from yesterday\'s required queue'
          : 'Orders still left from this required queue',
        helper: activeIssueOrdersInQueue > 0
          ? `${requiredDispatchFraction} dispatched vs current required queue • ${queueIssueHelper}`
          : `${requiredDispatchFraction} dispatched vs current required queue`,
        icon: <LocalShippingIcon fontSize="small" />,
        tone: Number(remainingRequiredDispatchOrders || 0) > 0 ? 'warning' : 'success',
      },
      {
        title: 'Overall Avg / Hr',
        value: formatRate(summary.avgOrdersPerHour),
        caption: 'Total dispatches divided by sessionized active operator hours',
        helper: totalActiveHoursLabel,
        icon: <SpeedIcon fontSize="small" />,
        tone: hasTarget ? getTargetTone(summary.teamTargetPct) : 'neutral',
      },
      {
        title: 'Peak Hour',
        value: formatNumber(summary.peakHourOrders),
        caption: 'Highest hourly dispatch total in range',
        icon: <InsightsIcon fontSize="small" />,
        tone: 'neutral',
      },
      {
        title: 'Overall Productivity %',
        value: formatTargetPercent(summary.teamTargetPct),
        caption: overallAvgVsTargetCaption,
        helper: overallProductivityHelper,
        icon: <QueryStatsIcon fontSize="small" />,
        tone: hasTarget ? getTargetTone(summary.teamTargetPct) : 'neutral',
      },
      {
        title: 'Hours Below Team Target',
        value: hoursBelowTargetValue,
        caption: `Completed hours below target within the ${workingHoursLabel} shift window`,
        helper: `Each counted hour compares actual dispatches vs active operators x ${formatNumber(operatorTargetPerHour)}/hr`,
        icon: <WarningAmberIcon fontSize="small" />,
        tone: hasTarget && Number(summary.hoursBelowTarget || 0) > 0 ? 'warning' : 'success',
      },
      {
        title: `SLA Attainment So Far${priorityScopeSuffix}`,
        value: formatPercent(summary.onTimePct),
        caption: 'Current on-time share of eligible orders in this view',
        helper: 'Undispatched eligible orders count against this until they are completed on time',
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        tone: Number(summary.onTimePct || 0) >= 98
          ? 'success'
          : Number(summary.onTimePct || 0) >= 95
            ? 'warning'
            : 'error',
      },
      {
        title: `Late Orders${priorityScopeSuffix}`,
        value: formatNumber(summary.lateCount),
        caption: 'Orders in this range that missed SLA',
        icon: <ScheduleIcon fontSize="small" />,
        tone: Number(summary.lateCount || 0) > 0 ? 'error' : 'success',
      },
    ];

    return historicalCards;
  }, [boardWorkingHoursPerDay, client, hasTarget, lens, livePacing, operatorRows.length, operatorTargetPerHour, priorityScopeSuffix, summary]);

  const heatmapMinMax = useMemo(() => {
    const minMax = {};
    for (const stat of HEATMAP_STATS) {
      let min = Infinity;
      let max = -Infinity;
      for (const hour of heatmapData.hours || []) {
        for (const category of heatmapData.categories || []) {
          const value = Number(hour?.[category.id]?.[stat] || 0);
          if (value < min) min = value;
          if (value > max) max = value;
        }
      }
      minMax[stat] = {
        min: Number.isFinite(min) ? min : 0,
        max: Number.isFinite(max) ? max : 0,
      };
    }
    return minMax;
  }, [heatmapData]);

  const heatmapHoursSorted = useMemo(
    () => [...(heatmapData.hours || [])].sort((left, right) => (left.hour < right.hour ? 1 : -1)),
    [heatmapData.hours]
  );

  const getHeatmapColor = useCallback((value, stat) => {
    const { min, max } = heatmapMinMax[stat] || { min: 0, max: 0 };
    if (max === min) return '#ffffff';
    let ratio = (Number(value || 0) - min) / (max - min);
    ratio = Math.sqrt(Math.max(0, Math.min(1, ratio)));
    const baseHex = HEATMAP_BASE_COLORS[stat];
    const white = 255;
    const red = Number.parseInt(baseHex.slice(1, 3), 16);
    const green = Number.parseInt(baseHex.slice(3, 5), 16);
    const blue = Number.parseInt(baseHex.slice(5, 7), 16);
    const blend = (channel) => Math.round(white - ((white - channel) * ratio));
    return `rgb(${blend(red)}, ${blend(green)}, ${blend(blue)})`;
  }, [heatmapMinMax]);

  const exportOperatorsCsv = useCallback(() => {
    const rows = [
      [
        'Operator',
        'Current Hour Orders',
        'Expected By Now',
        'Pace Delta',
        'Current Hour % of Target',
        'Overall Avg / Hr',
        'Overall Productivity %',
        'Best Hour Orders',
        'Hours Below Team Target',
        'Total Orders',
        'Operator-Active Hours',
        'Last Activity',
        'SLA On-Time %',
        'Late Orders',
        'Express Misses',
        'Standard Misses',
        'Productivity Status',
        'SLA Status',
      ],
      ...sortedOperatorRows.map((row) => ([
        row.operator,
        row.currentHourOrders ?? '',
        row.expectedByNow ?? '',
        row.paceDelta ?? '',
        row.currentHourTargetPct == null ? '' : (row.currentHourTargetPct * 100),
        row.avgOrdersPerHour ?? '',
        row.targetPct == null ? '' : (row.targetPct * 100),
        row.bestHourOrders ?? '',
        row.hoursBelowTarget ?? '',
        row.totalOrders ?? '',
        row.activeHours ?? '',
        row.lastActivity ?? '',
        row.slaOnTimePct ?? '',
        row.lateOrders ?? '',
        row.expressMisses ?? '',
        row.standardMisses ?? '',
        row.productivityStatus ?? '',
        row.slaStatus ?? '',
      ])),
    ];

    downloadCsv(`dispatch-control-board-operators-${client}-${lens}.csv`, rows);
  }, [client, lens, sortedOperatorRows]);

  const exportExceptionsCsv = useCallback(() => {
    const rows = [
      [
        'Status',
        'Order ID',
        'Order Number',
        'Client',
        'Required Dispatch Day',
        'Created At',
        'Dispatched Date',
        'Priority',
        'Operator',
        'Reason',
      ],
      ...exceptions.map((row) => ([
        getExceptionStatusLabel(row),
        row.orderId,
        row.orderNumber,
        row.clientName,
        row.requiredDispatchDay,
        row.createdAt,
        row.dispatchedDate,
        row.shippingPriority,
        row.operator,
        row.lateReason,
      ])),
    ];

    downloadCsv(`dispatch-control-board-exceptions-${client}-${lens}.csv`, rows);
  }, [client, exceptions, lens]);

  const exportHavingIssueCsv = useCallback(() => {
    const rows = [
      [
        'Issue Status',
        'Order ID',
        'Order Number',
        'Client',
        'Required Dispatch Day',
        'Created At',
        'Priority',
        'Issue Reason',
      ],
      ...havingIssueRows.map((row) => ([
        row.issueStatus,
        row.orderId,
        row.orderNumber,
        row.clientName,
        row.requiredDispatchDay,
        row.createdAt,
        row.shippingPriority,
        row.issueComments,
      ])),
    ];

    downloadCsv(`dispatch-control-board-issues-${client}-${lens}.csv`, rows);
  }, [client, havingIssueRows, lens]);

  const closeOperatorDrilldown = useCallback(() => {
    setSelectedOperatorDrilldown(null);
  }, []);

  const handleOperatorSort = (column) => {
    if (operatorSortBy === column) {
      setOperatorSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setOperatorSortBy(column);
    setOperatorSortDir(column === 'operator' ? 'asc' : 'desc');
  };

  const emptyState = !boardLoading
    && !boardError
    && controlBoard
    && summary.ordersDispatched === 0
    && exceptions.length === 0
    && havingIssueRows.length === 0;
  const visibleExceptions = exceptions.slice(0, 50);

  return (
    <Layout>
      <Container maxWidth={false} sx={{ maxWidth: '100%', px: { xs: 0.5, sm: 0.75, lg: 1 }, py: { xs: 1.25, md: 2 } }}>
        <Dialog
          open={targetManagerOpen}
          onClose={closeTargetManager}
          fullWidth
          maxWidth="lg"
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <DialogTitle
            sx={{
              pb: 1.1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.015)} 100%)`,
            }}
          >
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Manage Client Targets
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Update target per hour for multiple clients and save them together.
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: '8px !important' }}>
            <Stack spacing={1.25}>
              <TextField
                size="small"
                placeholder="Search client"
                value={targetManagerSearch}
                onChange={(event) => setTargetManagerSearch(event.target.value)}
              />

              {targetManagerError ? <Alert severity="error">{targetManagerError}</Alert> : null}

              {targetManagerLoading ? (
                <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems="stretch"
                  sx={{ minHeight: { md: 500 } }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      width: { md: 320 },
                      flexShrink: 0,
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.25,
                        py: 1.2,
                        background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.015)} 100%)`,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            display: 'grid',
                            placeItems: 'center',
                            color: 'primary.main',
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.07),
                          }}
                        >
                          <SpeedIcon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Client Targets
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Select one client to edit. Changed targets and validation issues stay visible until you save.
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${targetManagerClientRows.length} clients`}
                          sx={{
                            borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                            color: 'text.primary',
                          }}
                        />
                        {changedTargetRows.length ? (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`${changedTargetRows.length} changed`}
                          />
                        ) : null}
                        {targetManagerErrorCount ? (
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={`${targetManagerErrorCount} need attention`}
                          />
                        ) : null}
                      </Stack>
                      <Stack spacing={0.45} sx={{ mt: 1.1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Client list filter
                        </Typography>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={targetManagerListFilter}
                          onChange={(_event, nextValue) => {
                            if (nextValue) setTargetManagerListFilter(nextValue);
                          }}
                          sx={{
                            alignSelf: 'flex-start',
                            '& .MuiToggleButton-root': {
                              px: 1.1,
                              py: 0.45,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              borderColor: 'divider',
                              color: 'text.secondary',
                            },
                            '& .Mui-selected': {
                              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                              color: 'text.primary',
                            },
                          }}
                        >
                          <ToggleButton value="all">All</ToggleButton>
                          <ToggleButton value="changed">Changed</ToggleButton>
                          <ToggleButton value="attention">Needs attention</ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>
                    </Box>
                    <Divider />
                    <List
                      disablePadding
                      sx={{
                        maxHeight: { xs: 320, md: 480 },
                        overflowY: 'auto',
                      }}
                    >
                      {filteredTargetManagerClients.length ? filteredTargetManagerClients.map((row, index) => {
                        const isSelected = selectedTargetClientId === row.clientId;

                        return (
                          <React.Fragment key={row.clientId}>
                            <ListItemButton
                              selected={isSelected}
                              onClick={() => setSelectedTargetClientId(row.clientId)}
                              sx={{
                                alignItems: 'stretch',
                                px: 1.25,
                                py: 1.05,
                                borderLeft: '2px solid transparent',
                                transition: 'background-color 160ms ease, border-color 160ms ease',
                                '&:hover': {
                                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.03),
                                },
                                '&.Mui-selected': {
                                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.055),
                                  borderLeftColor: 'primary.main',
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.07),
                                },
                              }}
                            >
                              <Stack spacing={0.7} sx={{ width: '100%', minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {row.clientName}
                                </Typography>
                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label={`Current ${formatNumber(row.currentTarget)}/hr`}
                                    sx={isSelected ? {
                                      backgroundColor: (theme) => alpha(theme.palette.info.main, 0.05),
                                    } : undefined}
                                  />
                                  {row.isDirty ? (
                                    <Chip size="small" color="warning" variant="outlined" label="Unsaved" />
                                  ) : null}
                                  {row.hasError ? (
                                    <Chip size="small" color="error" variant="outlined" label="Needs attention" />
                                  ) : null}
                                </Stack>
                              </Stack>
                            </ListItemButton>
                            {index < filteredTargetManagerClients.length - 1 ? <Divider /> : null}
                          </React.Fragment>
                        );
                      }) : (
                        <Box sx={{ px: 1.5, py: 2.25 }}>
                          <Typography variant="body2" color="text.secondary">
                            No clients matched your current search and filter.
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      borderRadius: 2.5,
                      p: { xs: 1.25, md: 1.5 },
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      borderColor: 'divider',
                      background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.025)} 0%, ${alpha(theme.palette.background.paper, 1)} 28%)`,
                    }}
                  >
                    {selectedTargetClientRow ? (
                      <>
                        <Stack
                          direction={{ xs: 'column', lg: 'row' }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', lg: 'center' }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {selectedTargetClientRow.clientName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Update the dispatch target per hour for this client. Save All will persist every changed target together.
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                            <Chip
                              size="small"
                              color="info"
                              variant="outlined"
                              label={`Current ${formatNumber(selectedTargetClientRow.currentTarget)}/hr`}
                              sx={{
                                backgroundColor: (theme) => alpha(theme.palette.info.main, 0.05),
                              }}
                            />
                            {selectedTargetClientRow.isDirty ? (
                              <Chip size="small" color="warning" variant="outlined" label="Unsaved" />
                            ) : null}
                            {selectedTargetClientRow.hasError ? (
                              <Chip size="small" color="error" variant="outlined" label="Needs attention" />
                            ) : null}
                          </Stack>
                        </Stack>

                        <Divider />

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                            gap: 1,
                          }}
                        >
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              borderColor: (theme) => alpha(theme.palette.info.main, 0.14),
                              backgroundColor: (theme) => alpha(theme.palette.info.main, 0.04),
                            }}
                          >
                            <Stack spacing={0.35}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <QueryStatsIcon sx={{ fontSize: 18, color: 'info.main' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'info.dark' }}>
                                  Current Target
                                </Typography>
                              </Stack>
                              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                {formatNumber(selectedTargetClientRow.currentTarget)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                orders per hour
                              </Typography>
                            </Stack>
                          </Paper>

                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              borderColor: (theme) => alpha(theme.palette.primary.main, 0.14),
                              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                            }}
                          >
                            <Stack spacing={0.35}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <SpeedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                  Draft Target
                                </Typography>
                              </Stack>
                              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                {selectedTargetDraftValue != null ? formatNumber(selectedTargetDraftValue) : '--'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                current input value
                              </Typography>
                            </Stack>
                          </Paper>

                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              borderColor: (theme) => alpha(
                                selectedTargetDelta == null
                                  ? theme.palette.warning.main
                                  : selectedTargetDelta === 0
                                    ? theme.palette.success.main
                                    : theme.palette.warning.main,
                                0.14
                              ),
                              backgroundColor: (theme) => alpha(
                                selectedTargetDelta == null
                                  ? theme.palette.warning.main
                                  : selectedTargetDelta === 0
                                    ? theme.palette.success.main
                                    : theme.palette.warning.main,
                                0.04
                              ),
                            }}
                          >
                            <Stack spacing={0.35}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <AutorenewIcon
                                  sx={{
                                    fontSize: 18,
                                    color: selectedTargetDelta === 0 ? 'success.main' : 'warning.main',
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: selectedTargetDelta === 0 ? 'success.dark' : 'warning.dark',
                                  }}
                                >
                                  Difference
                                </Typography>
                              </Stack>
                              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                {selectedTargetDelta == null ? '--' : `${selectedTargetDelta > 0 ? '+' : ''}${formatNumber(selectedTargetDelta)}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                vs current target
                              </Typography>
                            </Stack>
                          </Paper>

                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.1,
                              borderRadius: 2,
                              borderColor: (theme) => alpha(theme.palette.text.primary, 0.10),
                              backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.025),
                            }}
                          >
                            <Stack spacing={0.35}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                  Valid Range
                                </Typography>
                              </Stack>
                              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                {MIN_DISPATCH_TARGET}-{MAX_DISPATCH_TARGET}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                allowed per hour
                              </Typography>
                            </Stack>
                          </Paper>
                        </Box>

                        <Stack spacing={1}>
                          <TextField
                            size="small"
                            label="Target / Hour"
                            value={selectedTargetClientRow.rawValue}
                            onChange={(event) => handleTargetDraftChange(selectedTargetClientRow.clientId, event.target.value)}
                            error={selectedTargetClientRow.hasError}
                            helperText={
                              selectedTargetClientRow.hasError
                                ? `Use ${MIN_DISPATCH_TARGET}-${MAX_DISPATCH_TARGET}`
                                : selectedTargetClientRow.isDirty
                                  ? 'Unsaved change'
                                  : `Allowed range ${MIN_DISPATCH_TARGET}-${MAX_DISPATCH_TARGET}`
                            }
                            inputProps={{
                              inputMode: 'numeric',
                              min: MIN_DISPATCH_TARGET,
                              max: MAX_DISPATCH_TARGET,
                            }}
                            sx={{
                              width: { xs: '100%', sm: 190 },
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.92),
                              },
                            }}
                          />
                          <Alert
                            severity={selectedTargetClientRow.hasError ? 'error' : selectedTargetClientRow.isDirty ? 'warning' : 'info'}
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              alignItems: 'flex-start',
                              '& .MuiAlert-message': { width: '100%' },
                              backgroundColor: (theme) => alpha(
                                selectedTargetClientRow.hasError
                                  ? theme.palette.error.main
                                  : selectedTargetClientRow.isDirty
                                    ? theme.palette.warning.main
                                    : theme.palette.info.main,
                                0.03
                              ),
                            }}
                          >
                            This target is used as the client dispatch target per hour on the KPI control board.
                            {selectedTargetClientRow.hasError
                              ? ` Enter a value between ${MIN_DISPATCH_TARGET} and ${MAX_DISPATCH_TARGET}.`
                              : selectedTargetClientRow.isDirty
                                ? ' Remember to use Save All when you are done reviewing changes.'
                                : ' Adjust it here if you need to tune throughput expectations for this client.'}
                          </Alert>
                        </Stack>
                      </>
                    ) : (
                      <Box sx={{ py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Select a client to edit its target.
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Stack>
              )}

              {!targetManagerLoading ? (
                <Typography variant="caption" color="text.secondary">
                  {changedTargetRows.length
                    ? `${formatNumber(changedTargetRows.length)} client target${changedTargetRows.length === 1 ? '' : 's'} ready to save.`
                    : 'No unsaved target changes.'}
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.25 }}>
            <Button onClick={closeTargetManager} disabled={targetManagerSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={targetManagerSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveTargetManager}
              disabled={targetManagerLoading || targetManagerSaving || !changedTargetRows.length}
            >
              {targetManagerSaving ? 'Saving...' : 'Save All'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(selectedOperatorDrilldown)}
          onClose={closeOperatorDrilldown}
          fullWidth
          maxWidth="lg"
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ pb: 1.1 }}>
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                SLA Drilldown{selectedOperatorDrilldown?.operator ? `: ${selectedOperatorDrilldown.operator}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Orders counted as SLA misses for this operator in the current board view.
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: '8px !important' }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip size="small" label={`SLA ${formatPercent(selectedOperatorDrilldown?.slaOnTimePct)}`} />
                <Chip size="small" label={`Status ${STATUS_LABELS[String(selectedOperatorDrilldown?.slaStatus || 'NEUTRAL').toUpperCase()] || '--'}`} />
                <Chip size="small" color={selectedOperatorMissSummary.total > 0 ? 'error' : 'default'} label={`Missed Orders ${formatNumber(selectedOperatorMissSummary.total)}`} />
                <Chip size="small" label={`Express Misses ${formatNumber(selectedOperatorMissSummary.express)}`} />
                <Chip size="small" label={`Standard Misses ${formatNumber(selectedOperatorMissSummary.standard)}`} />
              </Stack>

              <Alert severity="info">
                Orders whose latest issue status is `ON HOLD`, `IN PROGRESS`, `RESOLVED`, or `URGENT` are excluded from SLA by default on this board.
              </Alert>

              <TableContainer sx={{ maxHeight: 460, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: 1, px: { xs: 1, md: 1.25 } } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Required Dispatch Day</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Dispatched</TableCell>
                      <TableCell>Priority</TableCell>
                      {!hideSecondaryTableColumns ? <TableCell>Reason</TableCell> : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOperatorMissRows.length ? selectedOperatorMissRows.map((row) => (
                      <TableRow key={`drilldown-${row.orderId}`} hover>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.isExpress ? 'Missed Express SLA' : 'Missed Standard SLA'}
                            sx={row.isExpress
                              ? { backgroundColor: 'rgba(211, 47, 47, 0.12)', color: '#b71c1c', fontWeight: 700 }
                              : { backgroundColor: 'rgba(245, 124, 0, 0.12)', color: '#ad5d00', fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell>{row.orderNumber || row.orderId}</TableCell>
                        <TableCell>{row.clientName}</TableCell>
                        <TableCell>{row.requiredDispatchDay}</TableCell>
                        <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                        <TableCell>{formatDateTime(row.dispatchedDate)}</TableCell>
                        <TableCell>{row.shippingPriority || '--'}</TableCell>
                        {!hideSecondaryTableColumns ? <TableCell>{row.lateReason || '--'}</TableCell> : null}
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={hideSecondaryTableColumns ? 7 : 8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No SLA-miss orders are currently attributed to this operator in the selected view.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeOperatorDrilldown}>Close</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={reportManagerOpen}
          onClose={closeReportManager}
          fullWidth
          maxWidth="lg"
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ pb: 1.1 }}>
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Manage Daily KPI PDF Schedules
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One recipient now gets one daily digest email with all assigned PDFs. Manage the global digest send time, recipient lists, and preview emails before the scheduled cron runs.
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ pt: '8px !important' }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  size="small"
                  placeholder="Search client"
                  value={reportManagerSearch}
                  onChange={(event) => setReportManagerSearch(event.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Preview Date"
                  type="date"
                  value={reportPreviewDate}
                  onChange={(event) => setReportPreviewDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { sm: 180 } }}
                />
              </Stack>

              {reportManagerError ? <Alert severity="error">{reportManagerError}</Alert> : null}

              {reportManagerLoading ? (
                <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems="stretch"
                  sx={{ minHeight: { md: 540 } }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      width: { md: 320 },
                      flexShrink: 0,
                      borderRadius: 2.5,
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ px: 1.25, py: 1.15 }}>
                      <Stack spacing={1} sx={{ mb: 1.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Daily Digest Send Time
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          This single UK time controls when scheduled digest emails are sent to every recipient.
                        </Typography>
                        <TextField
                          size="small"
                          type="time"
                          label="Daily Digest Send Time (UK)"
                          value={normalizeReportTimeValue(dailyDigestReportTimeDraft)}
                          onChange={(event) => setDailyDigestReportTimeDraft(event.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 300 }}
                          error={hasDailyDigestReportTimeError}
                          helperText={hasDailyDigestReportTimeError ? 'Use HH:mm' : 'Default 18:00'}
                          sx={{ maxWidth: 220 }}
                        />
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                          <Chip size="small" color="primary" variant="outlined" label={`${normalizeReportTimeValue(dailyDigestReportTimeDraft)} UK`} />
                          {isDailyDigestReportTimeDirty ? (
                            <Chip size="small" color="warning" variant="outlined" label="Unsaved" />
                          ) : null}
                        </Stack>
                      </Stack>

                      <Divider sx={{ mb: 1.25 }} />

                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Scheduled Reports
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enabled schedules appear first. Select one report to edit details without scrolling through every client.
                      </Typography>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                        <Chip size="small" color="success" variant="outlined" label={`${enabledReportManagerClientCount} enabled`} />
                        <Chip size="small" variant="outlined" label={`${reportManagerClientRows.length} clients`} />
                        {reportManagerClientErrorCount ? (
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={`${reportManagerClientErrorCount} need attention`}
                          />
                        ) : null}
                        {totalDirtyReportChanges ? (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`${totalDirtyReportChanges} unsaved`}
                          />
                        ) : null}
                      </Stack>
                      <Stack spacing={0.45} sx={{ mt: 1.1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Client list filter
                        </Typography>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={reportManagerListFilter}
                          onChange={(_event, nextValue) => {
                            if (nextValue) setReportManagerListFilter(nextValue);
                          }}
                          sx={{
                            alignSelf: 'flex-start',
                            '& .MuiToggleButton-root': {
                              px: 1.1,
                              py: 0.45,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                            },
                          }}
                        >
                          <ToggleButton value="all">All</ToggleButton>
                          <ToggleButton value="enabled">Enabled</ToggleButton>
                          <ToggleButton value="attention">Needs attention</ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>
                    </Box>
                    <Divider />
                    <List
                      disablePadding
                      sx={{
                        maxHeight: { xs: 320, md: 520 },
                        overflowY: 'auto',
                      }}
                    >
                      <ListItemButton
                        selected={isAllClientsReportSelected}
                        onClick={() => setSelectedReportConfigId(ALL_CLIENTS_REPORT_KEY)}
                        sx={{
                          alignItems: 'stretch',
                          px: 1.25,
                          py: 1.1,
                          '&.Mui-selected': {
                            backgroundColor: 'action.selected',
                            borderLeft: '3px solid',
                            borderLeftColor: 'primary.main',
                          },
                          '&.Mui-selected:hover': {
                            backgroundColor: 'action.selected',
                          },
                        }}
                      >
                        <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
                          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                All Clients Overall Report
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Management summary PDF attached only for recipients explicitly listed here
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              color="primary"
                              variant={isAllClientsReportSelected ? 'filled' : 'outlined'}
                              label="Overall"
                            />
                          </Stack>
                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                            <Chip
                              size="small"
                              color={allClientsReportDraft.enabled ? 'success' : 'default'}
                              variant={allClientsReportDraft.enabled ? 'filled' : 'outlined'}
                              label={allClientsReportDraft.enabled ? 'Enabled' : 'Disabled'}
                            />
                            {allClientsReportRecipients.length ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${allClientsReportRecipients.length} recipient${allClientsReportRecipients.length === 1 ? '' : 's'}`}
                              />
                            ) : null}
                            <Chip
                              size="small"
                              variant="outlined"
                                    label={`Legacy ${normalizeReportTimeValue(allClientsReportDraft.time)} UK`}
                            />
                            {isAllClientsReportDirty ? (
                              <Chip size="small" color="warning" variant="outlined" label="Unsaved" />
                            ) : null}
                            {(allClientsReportInvalidRecipients.length > 0 || allClientsReportHasMissingRecipients || allClientsReportHasInvalidTime) ? (
                              <Chip size="small" color="error" variant="outlined" label="Needs attention" />
                            ) : null}
                          </Stack>
                        </Stack>
                      </ListItemButton>
                      <Divider />
                      {filteredReportManagerClients.length ? filteredReportManagerClients.map((row, index) => {
                        const isSelected = selectedReportConfigId === row.clientId;

                        return (
                          <React.Fragment key={row.clientId}>
                            <ListItemButton
                              selected={isSelected}
                              onClick={() => setSelectedReportConfigId(row.clientId)}
                              sx={{
                                alignItems: 'stretch',
                                px: 1.25,
                                py: 1.05,
                                '&.Mui-selected': {
                                  backgroundColor: 'action.selected',
                                  borderLeft: '3px solid',
                                  borderLeftColor: 'primary.main',
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: 'action.selected',
                                },
                              }}
                            >
                              <Stack spacing={0.7} sx={{ width: '100%', minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {row.clientName}
                                </Typography>
                                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    color={row.draft.enabled ? 'success' : 'default'}
                                    variant={row.draft.enabled ? 'filled' : 'outlined'}
                                    label={row.draft.enabled ? 'Enabled' : 'Disabled'}
                                  />
                                  {row.normalizedRecipients.length ? (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`${row.normalizedRecipients.length} recipient${row.normalizedRecipients.length === 1 ? '' : 's'}`}
                                    />
                                  ) : null}
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Legacy ${normalizeReportTimeValue(row.draft.time)} UK`}
                                  />
                                  {row.isDirty ? (
                                    <Chip size="small" color="warning" variant="outlined" label="Unsaved" />
                                  ) : null}
                                  {row.hasError ? (
                                    <Chip size="small" color="error" variant="outlined" label="Needs attention" />
                                  ) : null}
                                </Stack>
                              </Stack>
                            </ListItemButton>
                            {index < filteredReportManagerClients.length - 1 ? <Divider /> : null}
                          </React.Fragment>
                        );
                      }) : (
                        <Box sx={{ px: 1.5, py: 2.25 }}>
                          <Typography variant="body2" color="text.secondary">
                            No clients matched your current search and filter.
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Paper>

                  <Paper
                    variant="outlined"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      borderRadius: 2.5,
                      p: { xs: 1.25, md: 1.5 },
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', lg: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', lg: 'center' }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {selectedReportTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedReportDescription}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        <Chip
                          size="small"
                          color={selectedReportDraft.enabled ? 'success' : 'default'}
                          variant={selectedReportDraft.enabled ? 'filled' : 'outlined'}
                          label={selectedReportDraft.enabled ? 'Enabled' : 'Disabled'}
                        />
                        {selectedReportRecipients.length ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${selectedReportRecipients.length} recipient${selectedReportRecipients.length === 1 ? '' : 's'}`}
                          />
                        ) : null}
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${normalizeReportTimeValue(selectedReportDraft.time)} UK`}
                        />
                        {selectedReportIsDirty ? (
                          <Chip size="small" color="warning" variant="outlined" label="Unsaved" />
                        ) : null}
                        {selectedReportHasError ? (
                          <Chip size="small" color="error" variant="outlined" label="Needs attention" />
                        ) : null}
                      </Stack>
                    </Stack>

                    <Divider />

                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems={{ lg: 'flex-start' }}>
                      <FormControlLabel
                        sx={{ m: 0, minWidth: 150 }}
                        control={(
                          <Switch
                            checked={Boolean(selectedReportDraft.enabled)}
                            onChange={(event) => {
                              if (isAllClientsReportSelected) {
                                handleAllClientsReportDraftChange('enabled', event.target.checked);
                              } else if (selectedClientReportRow) {
                                handleReportScheduleEnabledChange(selectedClientReportRow.clientId, event.target.checked);
                              }
                            }}
                          />
                        )}
                        label="Enable Daily PDF"
                      />

                      <TextField
                        size="small"
                        type="time"
                        label={isAllClientsReportSelected ? 'Legacy Overall Time (UK)' : 'Legacy Client Time (UK)'}
                        value={normalizeReportTimeValue(selectedReportDraft.time)}
                        onChange={(event) => {
                          if (isAllClientsReportSelected) {
                            handleAllClientsReportDraftChange('time', event.target.value);
                          } else if (selectedClientReportRow) {
                            handleReportTimeChange(selectedClientReportRow.clientId, event.target.value);
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        error={selectedReportHasInvalidTime}
                        helperText={
                          selectedReportHasInvalidTime
                            ? 'Use HH:mm'
                            : (selectedReportIsDirty ? 'Unsaved legacy value' : 'Stored for compatibility only; scheduled digest uses the global time above.')
                        }
                        sx={{ width: { xs: '100%', sm: 190 } }}
                      />

                      <Box sx={{ flexGrow: 1 }} />

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', lg: 'auto' } }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={
                            reportTestDownloadingClientId === selectedReportManagerRow?.clientId
                              ? <CircularProgress size={16} color="inherit" />
                              : <DownloadIcon />
                          }
                          onClick={() => selectedReportManagerRow && handleDownloadTestReport(selectedReportManagerRow)}
                          disabled={
                            !selectedReportManagerRow
                            || reportManagerSaving
                            || selectedReportHasInvalidTime
                            || (Boolean(reportTestDownloadingClientId) && reportTestDownloadingClientId !== selectedReportManagerRow?.clientId)
                            || (Boolean(reportTestSendingClientId) && reportTestSendingClientId !== selectedReportManagerRow?.clientId)
                          }
                          sx={{ minWidth: { sm: 156 } }}
                        >
                          {reportTestDownloadingClientId === selectedReportManagerRow?.clientId ? 'Downloading...' : 'Download Test PDF'}
                        </Button>
                        {canManageUsers ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              reportTestSendingClientId === selectedReportManagerRow?.clientId
                                ? <CircularProgress size={16} color="inherit" />
                                : <SendIcon />
                            }
                            onClick={() => selectedReportManagerRow && handleSendTestReport(selectedReportManagerRow)}
                            disabled={
                              !selectedReportManagerRow
                              || reportManagerSaving
                              || selectedReportHasInvalidTime
                              || (Boolean(reportTestSendingClientId) && reportTestSendingClientId !== selectedReportManagerRow?.clientId)
                              || Boolean(reportTestDownloadingClientId)
                            }
                            sx={{ minWidth: { sm: 124 } }}
                          >
                            {reportTestSendingClientId === selectedReportManagerRow?.clientId ? 'Sending...' : 'Send Test'}
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>

                    <TextField
                      size="small"
                      label="Recipient Emails"
                      multiline
                      minRows={10}
                      placeholder="management@example.com; ops@example.com"
                      value={selectedReportDraft.recipients}
                      onChange={(event) => {
                        if (isAllClientsReportSelected) {
                          handleAllClientsReportDraftChange('recipients', event.target.value);
                        } else if (selectedClientReportRow) {
                          handleReportRecipientsChange(selectedClientReportRow.clientId, event.target.value);
                        }
                      }}
                      error={selectedReportInvalidRecipients.length > 0 || selectedReportHasMissingRecipients}
                      helperText={
                        selectedReportInvalidRecipients.length
                          ? `Invalid: ${selectedReportInvalidRecipients.join(', ')}`
                          : selectedReportHasMissingRecipients
                            ? 'At least one email is required when enabled.'
                            : (selectedReportIsDirty ? 'Unsaved change' : 'Use commas, semicolons, or new lines.')
                      }
                      fullWidth
                    />

                    <Typography variant="caption" color="text.secondary">
                      Preview date: {formattedReportPreviewDate}. Download Test PDF uses the selected preview date and current draft send time. {canManageUsers
                        ? `Admin test sends go only to ${TEST_KPI_REPORT_EMAIL}.`
                        : 'Only admins can trigger a live email send.'}
                    </Typography>
                  </Paper>
                </Stack>
              )}

              {!reportManagerLoading ? (
                <Typography variant="caption" color="text.secondary">
                  {totalDirtyReportChanges
                    ? `${formatNumber(totalDirtyReportChanges)} report schedule${totalDirtyReportChanges === 1 ? '' : 's'} ready to save.`
                    : 'No unsaved report schedule changes.'}
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.25 }}>
            <Button
              onClick={closeReportManager}
              disabled={reportManagerSaving || Boolean(reportTestSendingClientId) || Boolean(reportTestDownloadingClientId)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={reportManagerSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveReportManager}
              disabled={reportManagerLoading || reportManagerSaving || !totalDirtyReportChanges}
            >
              {reportManagerSaving ? 'Saving...' : 'Save All'}
            </Button>
          </DialogActions>
        </Dialog>

        <Stack spacing={2.25}>
        <Paper
          variant="outlined"
          sx={{
              p: { xs: 1.25, md: 1.45, xl: 1.6 },
              borderRadius: 4,
              background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%)',
              borderColor: 'rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box
              sx={{
            display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 1.02fr) minmax(0, 1.58fr)' },
                gap: { xs: 1.25, lg: 1 },
                alignItems: 'start',
              }}
            >
              <Box
                sx={{
                  p: { xs: 0, lg: 0.2 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 1,
                  minWidth: 0,
          }}
        >
          <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.45,
                      color: 'primary.main',
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      fontWeight: 700,
                      fontSize: 10.5,
                    }}
                  >
                    Dispatch Control
            </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, lineHeight: 1.05, mb: 0.45, fontSize: { xs: 20, md: 22 } }}
                  >
                    Dispatch Performance Control Board
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 520, fontSize: { xs: 12, md: 12.5 }, lineHeight: 1.4 }}
                  >
                    Real-time operator throughput and SLA quality for dispatch leads. Built to show who needs intervention, where client risk is building, and whether the team is recovering hour by hour.
            </Typography>
          </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                    gap: 0.65,
                  }}
                >
                  <Paper variant="outlined" sx={{ p: { xs: 0.8, xl: 0.75 }, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.85)' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.45, fontWeight: 700, fontSize: 9.75 }}>
                      Client Scope
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.1, fontSize: 12.5 }}>
                      {client === 'ALL'
                        ? activeExcludedAllClientNames.length
                          ? `All clients excl. ${formatExcludedClientsLabel(activeExcludedAllClientNames)}`
                          : 'All clients'
                        : client}
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: { xs: 0.8, xl: 0.75 }, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.85)' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.45, fontWeight: 700, fontSize: 9.75 }}>
                      Time Lens
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.1, fontSize: 12.5 }}>
                      {lens === 'selected-day'
                        ? formatDateRangeLabel(startDate, endDate)
                        : lens === 'yesterday'
                          ? 'Yesterday'
                          : lens === 'rolling'
                            ? `Rolling ${windowHours}h`
                            : 'Today live'}
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: { xs: 0.8, xl: 0.75 }, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.85)' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.45, fontWeight: 700, fontSize: 9.75 }}>
                      SLA Scope
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.1, fontSize: 12.5 }}>
                      {priority}
                    </Typography>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: { xs: 0.8, xl: 0.75 }, borderRadius: 2.5, backgroundColor: 'rgba(255,255,255,0.85)' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.45, fontWeight: 700, fontSize: 9.75 }}>
                      Target
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.1, fontSize: 12.5 }}>
                      {`${operatorTargetPerHour}/hr`}
                    </Typography>
                  </Paper>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.25, lineHeight: 1.3 }}>
                  Last updated: {formatLastUpdated(lastUpdated)}. Productivity uses session-based active dispatch time, with a new session after 30 minutes idle. Priority toggles affect SLA, risk, and exceptions only.
                  {activeExcludedAllClientNames.length ? ` ALL view excludes ${activeExcludedAllClientNames.join(', ')}.` : ''}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 0.75,
                  alignContent: 'start',
                }}
              >
                <ControlGroup label="Scope" emphasis>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
                    <FormControl size="small">
            <InputLabel>Client</InputLabel>
                      <Select
                        value={client}
                        label="Client"
                        onChange={(event) => setClient(event.target.value)}
                      >
              <MenuItem value="ALL">ALL</MenuItem>
                        {clientList.map((item) => (
                          <MenuItem key={item.clientName} value={item.clientName}>
                            {item.clientName}
                          </MenuItem>
                        ))}
            </Select>
          </FormControl>

                    <TextField
                      size="small"
                      label="Client Target / Hour"
                      value={operatorTargetPerHour}
                      disabled
                    />
                  </Box>
                  <FormControl size="small" fullWidth disabled={client !== 'ALL'}>
                    <InputLabel>Exclude From ALL</InputLabel>
                    <Select
                      multiple
                      value={excludedAllClientNames}
                      label="Exclude From ALL"
                      onChange={(event) => {
                        const value = event.target.value;
                        const nextValues = typeof value === 'string' ? value.split(',') : value;
                        setExcludedAllClientNames(Array.from(new Set(
                          nextValues
                            .map((item) => String(item || '').trim())
                            .filter(Boolean)
                        )));
                      }}
                      renderValue={(selected) => formatExcludedClientsLabel(selected)}
                    >
                      {allClientExclusionOptions.map((clientName) => (
                        <MenuItem key={clientName} value={clientName}>
                          <Checkbox size="small" checked={excludedAllClientNames.includes(clientName)} />
                          <ListItemText primary={clientName} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.25, lineHeight: 1.25 }}>
                    Applies only when Client is ALL. Default excludes Funky Pigeon from all board statistics.
                  </Typography>
                  {canManageClients ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: 0.75,
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ManageAccountsIcon />}
                        onClick={openTargetManager}
                        sx={{
                          minHeight: 31,
                          minWidth: 0,
                          width: '100%',
                          px: 1,
                          fontSize: 11,
                          lineHeight: 1.15,
                          whiteSpace: 'normal',
                          justifyContent: 'center',
                          '& .MuiButton-startIcon': {
                            ml: 0,
                            mr: 0.5,
                            flexShrink: 0,
                            '& svg': { fontSize: 16 },
                          },
                        }}
                      >
                        Manage Targets
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ScheduleIcon />}
                        onClick={openReportManager}
                        sx={{
                          minHeight: 31,
                          minWidth: 0,
                          width: '100%',
                          px: 1,
                          fontSize: 11,
                          lineHeight: 1.15,
                          whiteSpace: 'normal',
                          justifyContent: 'center',
                          '& .MuiButton-startIcon': {
                            ml: 0,
                            mr: 0.5,
                            flexShrink: 0,
                            '& svg': { fontSize: 16 },
                          },
                        }}
                      >
                        Report Schedules
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1', fontSize: 11 }}>
                        Edit targets, daily PDF recipients, and admin test sends here.
                      </Typography>
                    </Box>
                  ) : null}
                </ControlGroup>

                <ControlGroup label="Time Lens">
          <ToggleButtonGroup
            size="small"
                    value={lens}
            exclusive
                    fullWidth
                    onChange={(_, value) => value && setLens(value)}
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: { xs: 0.7, sm: 1.15 },
                        fontSize: { xs: 11, sm: 12.5 },
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="yesterday">Yesterday</ToggleButton>
                    <ToggleButton value="selected-day">Selected Day</ToggleButton>
                    <ToggleButton value="rolling">Rolling</ToggleButton>
                  </ToggleButtonGroup>

                  {lens === 'selected-day' ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
                      <TextField
                        size="small"
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        size="small"
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  ) : null}

                  {lens === 'rolling' ? (
                    <ToggleButtonGroup
                      size="small"
                      value={windowHours}
                      exclusive
                      fullWidth
                      onChange={(_, value) => value && setWindowHours(value)}
          >
            <ToggleButton value={6}>6h</ToggleButton>
            <ToggleButton value={12}>12h</ToggleButton>
            <ToggleButton value={24}>24h</ToggleButton>
            <ToggleButton value={48}>48h</ToggleButton>
          </ToggleButtonGroup>
                  ) : (
                    <Stack spacing={0.75}>
                      <Typography variant="caption" color="text.secondary">
                        {lens === 'today'
                          ? 'Live view shows the current in-progress hour plus session-based productivity.'
                          : lens === 'yesterday'
                            ? 'Yesterday uses a closed historical day with no live pacing or at-risk rows.'
                            : 'Historical mode compares completed hours and session-based productivity.'}
                      </Typography>
                      {!livePacing ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField
                            size="small"
                            label="Working Day Hours"
                            value={workingHoursDraft}
                            onChange={(event) => setWorkingHoursDraft(sanitizeWorkingHoursInput(event.target.value))}
                            sx={{ maxWidth: { sm: 150 } }}
                            helperText={`Saved default: ${formatHours(effectiveSavedWorkingHours, 1)}`}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleApplyWorkingHours}
                            sx={{ minWidth: { sm: 108 } }}
                          >
                            Apply
                          </Button>
                          {canManageClients ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleSaveWorkingHoursDefault}
                              disabled={workingHoursSaving}
                              startIcon={workingHoursSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                              sx={{ minWidth: { sm: 148 } }}
                            >
                              {workingHoursSaving ? 'Saving...' : 'Save Default'}
                            </Button>
                          ) : null}
                        </Stack>
                      ) : null}
                    </Stack>
                  )}
                </ControlGroup>

                <ControlGroup label="SLA Priority">
                  <ToggleButtonGroup
                    size="small"
                    value={priority}
                    exclusive
                    fullWidth
                    onChange={(_, value) => value && setPriority(value)}
                  >
                    <ToggleButton value="ALL">All</ToggleButton>
                    <ToggleButton value="EXPRESS">Express</ToggleButton>
                    <ToggleButton value="STANDARD">Standard</ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary">
                    Filters on-time, late, client risk, and exception panels only.
                  </Typography>
                </ControlGroup>

                <ControlGroup label="Refresh & Views">
          <FormControlLabel
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12.25 } }}
                    control={(
                      <Switch
                        checked={autoRefresh}
                        onChange={(event) => setAutoRefresh(event.target.checked)}
                      />
                    )}
                    label="Auto refresh (60s)"
                  />

                  <Stack direction="row" spacing={0.75}>
            <Button
                      variant={showHeatmap ? 'contained' : 'outlined'}
              size="small"
                      startIcon={<InsightsIcon />}
                      onClick={() => setShowHeatmap((current) => !current)}
                      disabled={!heatmapAvailable}
                      fullWidth
                    >
                      {showHeatmap ? 'Heatmap On' : 'Show Heatmap'}
            </Button>
            <Button
              variant="contained"
                      size="small"
                      startIcon={<AutorenewIcon />}
                      onClick={refreshAll}
                      disabled={boardLoading}
                      fullWidth
                    >
                      Refresh
            </Button>
                  </Stack>
                </ControlGroup>
          </Box>
            </Box>
          </Paper>

          {boardLoading && controlBoard ? <LinearProgress /> : null}

          {boardError ? (
            <Alert severity="error">{boardError}</Alert>
          ) : null}

          {emptyState ? (
            <Alert severity="info">
              No dispatch, exception, or issue-follow-up data was returned for the selected view.
            </Alert>
          ) : null}

          {boardLoading && !controlBoard ? (
            <SummaryStripSkeleton />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))',
                  xl: 'repeat(8, minmax(0, 1fr))',
                },
                gap: { xs: 1.2, lg: 0.9, xl: 0 },
                borderRadius: { xl: 3 },
                overflow: { xl: 'hidden' },
                border: { xl: '1px solid rgba(15, 23, 42, 0.08)' },
                backgroundColor: { xl: 'rgba(255,255,255,0.96)' },
              }}
            >
              {summaryCards.map((card, index) => (
                <SummaryMetricCard
                  key={card.title}
                  icon={card.icon}
                  title={card.title}
                  value={card.value}
                  valueAdornment={card.valueAdornment}
                  caption={card.caption}
                  helper={card.helper}
                  tone={card.tone}
                  banded={true}
                  isLast={index === summaryCards.length - 1}
                      />
                    ))}
                  </Box>
          )}

          <Paper variant="outlined" sx={{ borderRadius: 4, p: { xs: 1.75, md: 2, xl: 2.25 } }}>
            <SectionHeader
              eyebrow="Primary Action Surface"
              title="Operator Performance Board"
              subtitle="Session-based productivity and SLA view ranked so under-target operators surface first."
              action={(
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={0.75}
                  useFlexGap
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search operator"
                    value={operatorQuery}
                    onChange={(event) => setOperatorQuery(event.target.value)}
                    sx={{
                      minWidth: { xs: '100%', sm: 176, xl: 208 },
                      '& .MuiInputBase-root': { minHeight: 34 },
                      '& .MuiInputBase-input': { py: 0.8, fontSize: 12.5 },
                    }}
                  />
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportOperatorsCsv}
                    disabled={!sortedOperatorRows.length}
                    sx={{ minHeight: 34, px: 1.25, whiteSpace: 'nowrap' }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              )}
            />

            <Divider sx={{ my: 1.5 }} />

            {showActiveOperatorControls ? (
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1}
                useFlexGap
                sx={{ mb: 1.5, alignItems: { lg: 'center' }, justifyContent: 'space-between' }}
              >
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    label={`Active now ${formatNumber(operatorActivityCounts.active)}`}
                    sx={{ backgroundColor: 'rgba(46, 125, 50, 0.12)', color: '#1b5e20', fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    label={`Earlier today ${formatNumber(operatorActivityCounts.earlier)}`}
                    sx={{ backgroundColor: 'rgba(15, 23, 42, 0.06)', color: 'text.secondary', fontWeight: 700 }}
                  />
                </Stack>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={operatorActivityFilter}
                  onChange={(_, nextValue) => {
                    if (nextValue) setOperatorActivityFilter(nextValue);
                  }}
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 700,
                      px: 1.25,
                    },
                  }}
                >
                  <ToggleButton value="all">All operators</ToggleButton>
                  <ToggleButton value="active">Active now</ToggleButton>
                  <ToggleButton value="earlier">Earlier today</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            ) : null}

            <TableContainer
                          sx={{
                maxHeight: { lg: '64vh', xl: '68vh' },
                overflow: 'auto',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'rgba(248, 250, 252, 0.65)',
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  minWidth: hideSecondaryTableColumns ? 760 : 980,
                  '& .MuiTableCell-root': {
                    py: 1,
                    px: { xs: 1, md: 1.2 },
                    whiteSpace: 'nowrap',
                    fontSize: { xs: 12, md: 12.5 },
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: '#eef2f7',
                    fontSize: 10.5,
                    textTransform: 'uppercase',
                    letterSpacing: 0.55,
                            fontWeight: 700,
                    borderBottom: '1px solid rgba(15, 23, 42, 0.1)',
                            position: 'sticky',
                    top: 0,
                    zIndex: 3,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: { md: 'sticky' },
                        left: { md: 0 },
                        zIndex: { md: 6 },
                        backgroundColor: '#e7edf6',
                        borderBottom: '1px solid rgba(15, 23, 42, 0.12)',
                      }}
                    />
                    {livePacing ? (
                      <TableCell
                        align="center"
                        colSpan={4}
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.11)',
                          color: '#0f4fa8',
                          fontSize: 10.25,
                          letterSpacing: 0.45,
                          fontWeight: 800,
                          borderBottom: '1px solid rgba(15, 23, 42, 0.12)',
                          ...getOperatorGroupDividerSx(),
                        }}
                      >
                        Live Pace
                      </TableCell>
                    ) : (
                      <TableCell
                        align="center"
                        colSpan={2}
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.11)',
                          color: '#0f4fa8',
                          fontSize: 10.25,
                          letterSpacing: 0.45,
                          fontWeight: 800,
                          borderBottom: '1px solid rgba(15, 23, 42, 0.12)',
                          ...getOperatorGroupDividerSx(),
                        }}
                      >
                        Historical Pace
                      </TableCell>
                    )}
                    <TableCell
                      align="center"
                      colSpan={hideSecondaryTableColumns ? 3 : 5}
                      sx={{
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        color: '#1f6b2b',
                        fontSize: 10.25,
                        letterSpacing: 0.45,
                        fontWeight: 800,
                        borderBottom: '1px solid rgba(15, 23, 42, 0.12)',
                        ...getOperatorGroupDividerSx(),
                      }}
                    >
                      Session Productivity
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={3}
                      sx={{
                        backgroundColor: 'rgba(255, 167, 38, 0.11)',
                        color: '#9a5600',
                        fontSize: 10.25,
                        letterSpacing: 0.45,
                        fontWeight: 800,
                        borderBottom: '1px solid rgba(15, 23, 42, 0.12)',
                      }}
                    >
                      SLA
                    </TableCell>
                  </TableRow>
                      <TableRow>
                          <TableCell
                      sortDirection={operatorSortBy === 'operator' ? operatorSortDir : false}
                            sx={{
                        position: { md: 'sticky' },
                        left: { md: 0 },
                        zIndex: { md: 5 },
                        minWidth: { xs: 142, md: 156, xl: 168 },
                        backgroundColor: '#eef2f7',
                        boxShadow: { md: '1px 0 0 rgba(15, 23, 42, 0.08)' },
                      }}
                    >
                      <TableSortLabel
                        active={operatorSortBy === 'operator'}
                        direction={operatorSortBy === 'operator' ? operatorSortDir : 'asc'}
                        onClick={() => handleOperatorSort('operator')}
                      >
                        Operator
                      </TableSortLabel>
                          </TableCell>
                    {livePacing ? (
                      <>
                        <TableCell align="right" sortDirection={operatorSortBy === 'currentHourOrders' ? operatorSortDir : false}>
                          <TableSortLabel
                            active={operatorSortBy === 'currentHourOrders'}
                            direction={operatorSortBy === 'currentHourOrders' ? operatorSortDir : 'desc'}
                            onClick={() => handleOperatorSort('currentHourOrders')}
                          >
                            Current Hr Orders
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">Expected Now</TableCell>
                        <TableCell align="right">Delta</TableCell>
                        <TableCell
                          align="right"
                          sortDirection={operatorSortBy === 'currentHourTargetPct' ? operatorSortDir : false}
                          sx={getOperatorGroupDividerSx()}
                        >
                          <TableSortLabel
                            active={operatorSortBy === 'currentHourTargetPct'}
                            direction={operatorSortBy === 'currentHourTargetPct' ? operatorSortDir : 'desc'}
                            onClick={() => handleOperatorSort('currentHourTargetPct')}
                          >
                            Current Hour %
                          </TableSortLabel>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="right">Best Hour Orders</TableCell>
                        <TableCell align="right" sx={getOperatorGroupDividerSx()}>Hours Below Team Target</TableCell>
                      </>
                    )}
                    <TableCell align="right" sortDirection={operatorSortBy === 'avgOrdersPerHour' ? operatorSortDir : false}>
                      <TableSortLabel
                        active={operatorSortBy === 'avgOrdersPerHour'}
                        direction={operatorSortBy === 'avgOrdersPerHour' ? operatorSortDir : 'desc'}
                        onClick={() => handleOperatorSort('avgOrdersPerHour')}
                      >
                        Overall Avg / Hr
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={operatorSortBy === 'targetPct' ? operatorSortDir : false}>
                      <TableSortLabel
                        active={operatorSortBy === 'targetPct'}
                        direction={operatorSortBy === 'targetPct' ? operatorSortDir : 'desc'}
                        onClick={() => handleOperatorSort('targetPct')}
                      >
                        Overall Productivity %
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sortDirection={operatorSortBy === 'totalOrders' ? operatorSortDir : false}>
                      <TableSortLabel
                        active={operatorSortBy === 'totalOrders'}
                        direction={operatorSortBy === 'totalOrders' ? operatorSortDir : 'desc'}
                        onClick={() => handleOperatorSort('totalOrders')}
                      >
                        Total
                      </TableSortLabel>
                    </TableCell>
                    {!hideSecondaryTableColumns ? <TableCell align="right">Active Hrs</TableCell> : null}
                    {!hideSecondaryTableColumns ? (
                      <TableCell sortDirection={operatorSortBy === 'lastActivity' ? operatorSortDir : false}>
                        <TableSortLabel
                          active={operatorSortBy === 'lastActivity'}
                          direction={operatorSortBy === 'lastActivity' ? operatorSortDir : 'asc'}
                          onClick={() => handleOperatorSort('lastActivity')}
                        >
                          Last Activity
                        </TableSortLabel>
                      </TableCell>
                    ) : null}
                    <TableCell align="right" sortDirection={operatorSortBy === 'slaOnTimePct' ? operatorSortDir : false}>
                      <TableSortLabel
                        active={operatorSortBy === 'slaOnTimePct'}
                        direction={operatorSortBy === 'slaOnTimePct' ? operatorSortDir : 'desc'}
                        onClick={() => handleOperatorSort('slaOnTimePct')}
                      >
                        SLA %
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Productivity</TableCell>
                    <TableCell>SLA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                  {boardLoading && !sortedOperatorRows.length ? (
                    Array.from({ length: 8 }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: operatorTableColumnCount }).map((__, cellIndex) => (
                          <TableCell key={cellIndex}>
                            <Skeleton />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : sortedOperatorRows.length ? (
                    sortedOperatorRows.map((row) => {
                      const hasOperatorDrilldown = operatorSlaMisses.some(
                        (missRow) => normalizeOperatorKey(missRow.operator) === normalizeOperatorKey(row.operator)
                      );
                      const flagged = row.productivityStatus === 'RISK' || row.productivityStatus === 'WATCH' || row.slaStatus === 'RISK';
                      const rowAccent = row.slaStatus === 'RISK'
                        ? 'rgba(211, 47, 47, 0.9)'
                        : row.productivityStatus === 'RISK'
                          ? 'rgba(211, 47, 47, 0.85)'
                          : row.productivityStatus === 'WATCH'
                          ? 'rgba(245, 124, 0, 0.92)'
                          : 'transparent';
                      const rowBackground = row.slaStatus === 'RISK'
                        ? 'rgba(255, 235, 238, 0.44)'
                        : row.productivityStatus === 'RISK'
                          ? 'rgba(255, 235, 238, 0.34)'
                          : row.productivityStatus === 'WATCH'
                          ? 'rgba(255, 243, 224, 0.5)'
                          : undefined;
                      const stickyBackground = row.slaStatus === 'RISK'
                        ? 'rgba(255, 235, 238, 0.98)'
                        : row.productivityStatus === 'RISK'
                          ? 'rgba(255, 235, 238, 0.96)'
                          : row.productivityStatus === 'WATCH'
                          ? 'rgba(255, 243, 224, 0.98)'
                          : '#fff';
                      return (
                        <TableRow
                          key={row.operator}
                          hover
                          sx={{
                            backgroundColor: rowBackground,
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              position: { md: 'sticky' },
                              left: { md: 0 },
                              zIndex: { md: 2 },
                              backgroundColor: stickyBackground,
                              boxShadow: { md: '1px 0 0 rgba(15, 23, 42, 0.06)' },
                              pl: flagged ? 1.75 : undefined,
                              '&::before': flagged ? {
                                content: '""',
                                position: 'absolute',
                              left: 0,
                                top: 0,
                                bottom: 0,
                                width: 4,
                                backgroundColor: rowAccent,
                              } : undefined,
                            }}
                          >
                            <Stack spacing={0.4}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                {flagged ? (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: rowAccent,
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : null}
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {row.operator}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                Last: {formatDateTime(row.lastActivity)}
                              </Typography>
                              {showActiveOperatorControls ? (
                                <Box>
                                  <Chip
                                    size="small"
                                    label={row.isActiveNow ? 'Active now' : 'Earlier today'}
                                    sx={row.isActiveNow
                                      ? { backgroundColor: 'rgba(46, 125, 50, 0.12)', color: '#1b5e20', fontWeight: 700, height: 22 }
                                      : { backgroundColor: 'rgba(15, 23, 42, 0.06)', color: '#4b5563', fontWeight: 700, height: 22 }}
                                  />
                                </Box>
                              ) : null}
                            </Stack>
                          </TableCell>
                          {livePacing ? (
                            <>
                              <TableCell align="right">{formatNumber(row.currentHourOrders)}</TableCell>
                              <TableCell align="right">{formatNumber(row.expectedByNow, 1)}</TableCell>
                              <TableCell
                                align="right"
                                  sx={{
                                  fontWeight: 700,
                                  color: row.paceDelta < 0 ? 'error.main' : row.paceDelta > 0 ? 'success.main' : 'text.primary',
                                }}
                              >
                                {formatDelta(row.paceDelta)}
                              </TableCell>
                              <TableCell align="right">{formatTargetPercent(row.currentHourTargetPct)}</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell align="right">{formatNumber(row.bestHourOrders)}</TableCell>
                              <TableCell align="right">{controlBoardHasTarget ? formatNumber(row.hoursBelowTarget) : '--'}</TableCell>
                            </>
                          )}
                          <TableCell align="right">{formatRate(row.avgOrdersPerHour)}</TableCell>
                          <TableCell align="right">{controlBoardHasTarget ? formatTargetPercent(row.targetPct) : '--'}</TableCell>
                          <TableCell align="right">{formatNumber(row.totalOrders)}</TableCell>
                          {!hideSecondaryTableColumns ? <TableCell align="right">{formatNumber(row.activeHours, 1)}</TableCell> : null}
                    {!hideSecondaryTableColumns ? <TableCell sx={{ color: 'text.secondary', ...getOperatorGroupDividerSx() }}>{formatDateTime(row.lastActivity)}</TableCell> : null}
                    <TableCell align="right" sx={hideSecondaryTableColumns ? getOperatorGroupDividerSx() : undefined}>{formatPercent(row.slaOnTimePct)}</TableCell>
                          <TableCell><StatusChip value={row.productivityStatus} labels={PRODUCTIVITY_STATUS_LABELS} /></TableCell>
                          <TableCell>
                            {hasOperatorDrilldown ? (
                              <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap="wrap">
                                <Tooltip title="View orders counted against this operator SLA">
                                  <Box sx={{ display: 'inline-flex' }}>
                                    <StatusChip
                                      value={row.slaStatus}
                                      chipProps={{
                                        clickable: true,
                                        onClick: () => setSelectedOperatorDrilldown({
                                          operator: row.operator || 'IVuser',
                                          slaStatus: row.slaStatus,
                                          slaOnTimePct: row.slaOnTimePct,
                                          expressMisses: row.expressMisses,
                                          standardMisses: row.standardMisses,
                                        }),
                                        sx: {
                                          cursor: 'pointer',
                                          border: '1px solid rgba(25, 118, 210, 0.22)',
                                          boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.04)',
                                          '&:hover': {
                                            borderColor: 'primary.main',
                                            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.10)',
                                          },
                                        },
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setSelectedOperatorDrilldown({
                                    operator: row.operator || 'IVuser',
                                    slaStatus: row.slaStatus,
                                    slaOnTimePct: row.slaOnTimePct,
                                    expressMisses: row.expressMisses,
                                    standardMisses: row.standardMisses,
                                  })}
                                  sx={{
                                    minWidth: 'auto',
                                    px: 0.75,
                                    py: 0.2,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  View orders
                                </Button>
                              </Stack>
                            ) : (
                              <StatusChip value={row.slaStatus} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={operatorTableColumnCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No operators matched the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 4, p: { xs: 1.75, md: 2, xl: 2.5 } }}>
            <SectionHeader
              eyebrow="Shift Rhythm"
              title="Hourly Team Rhythm"
              subtitle="Hourly dispatch totals against the team target for that hour."
              action={(
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    icon={showHeatmap ? <InsightsIcon /> : <QueryStatsIcon />}
                    label={showHeatmap && heatmapAvailable ? 'Heatmap drilldown active' : 'Target vs dispatched orders chart'}
                  />
                  {!showHeatmap && controlBoardHasTarget ? (
                    <Chip
                      size="small"
                      label="Red <75% • Amber 75-99% • Green 100%+"
                      sx={{
                        backgroundColor: 'rgba(245, 124, 0, 0.08)',
                        color: 'text.secondary',
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                      }}
                    />
                  ) : null}
                </Stack>
              )}
            />

            <Divider sx={{ my: 2 }} />

            {showHeatmap && heatmapAvailable ? (
              <>
                {heatmapError ? <Alert severity="warning" sx={{ mb: 2 }}>{heatmapError}</Alert> : null}
                {heatmapLoading ? (
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : heatmapData.hours.length ? (
                  <TableContainer sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                    <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: 1 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            rowSpan={2}
                            sx={{
                              fontWeight: 700,
                              minWidth: 160,
                              backgroundColor: '#f8f8f8',
                              verticalAlign: 'middle',
                            }}
                          >
                            Hour
                          </TableCell>
                          {heatmapData.categories.map((category) => (
                            <TableCell
                              key={category.id}
                              align="center"
                              colSpan={HEATMAP_STATS.length}
                              sx={{ fontWeight: 700, backgroundColor: '#f4f4f4' }}
                            >
                              {category.name}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          {heatmapData.categories.flatMap((category) => HEATMAP_STATS.map((stat) => (
                            <TableCell
                              key={`${category.id}-${stat}`}
                              align="center"
                              sx={{ fontWeight: 700, backgroundColor: HEATMAP_BASE_COLORS[stat] }}
                            >
                              {HEATMAP_LABELS[stat]}
                            </TableCell>
                          )))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {heatmapHoursSorted.map((hour) => (
                          <TableRow key={hour.hour} hover>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {hour.hour}
                            </TableCell>
                            {heatmapData.categories.flatMap((category) => HEATMAP_STATS.map((stat) => {
                              const value = Number(hour?.[category.id]?.[stat] || 0);
                              return (
                                <TableCell
                                  key={`${hour.hour}-${category.id}-${stat}`}
                                    align="center"
                                    sx={{
                                    backgroundColor: getHeatmapColor(value, stat),
                                      fontWeight: 600,
                                    }}
                                  >
                                  {formatNumber(value)}
                                  </TableCell>
                              );
                            }))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">No heatmap data is available for the current live window.</Alert>
                )}
              </>
            ) : (
              <Box
                sx={{
                  height: isLaptopWidth ? 292 : 340,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1.5,
                  backgroundColor: 'rgba(248, 250, 252, 0.6)',
                }}
              >
                {hourlyChartData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyChartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <RechartsTooltip
                        formatter={(value, name, item) => {
                          const payload = item?.payload;
                          const suffix = payload?.targetPct == null
                            ? ''
                            : ` (${formatTargetPercent(payload.targetPct)})`;
                          const seriesLabel = name === 'Dispatched Orders'
                            ? 'Dispatched Orders (completed this hour)'
                            : name;
                          return [formatNumber(value), `${seriesLabel}${suffix}`];
                        }}
                      />
                      <Legend />
                      <Bar dataKey="actualOrders" name="Dispatched Orders" radius={[6, 6, 0, 0]}>
                        {hourlyChartData.map((entry) => (
                          <Cell
                            key={entry.hour}
                            fill={
                              entry.tone === 'error'
                                ? '#d32f2f'
                                : entry.tone === 'warning'
                                  ? '#f57c00'
                                  : controlBoardHasTarget
                                    ? '#4CBF88'
                                    : entry.isCurrentHour
                                      ? '#1976d2'
                                      : '#4CBF88'
                            }
                          />
                        ))}
                      </Bar>
                      {controlBoardHasTarget ? (
                        <Line
                          type="monotone"
                          dataKey="teamTargetOrders"
                          name="Team Target"
                          stroke="#f57c00"
                          strokeWidth={2}
                          dot={{ r: 2 }}
                        />
                      ) : null}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert severity="info">No hourly pace data is available for this view.</Alert>
                )}
              </Box>
            )}

            {!heatmapAvailable ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Heatmap drilldown is kept for live and rolling windows. Yesterday and selected-day stay focused on historical pace and SLA outcomes.
                    </Typography>
            ) : null}
                  </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.15fr 1fr' }, gap: { xs: 2, xl: 2.5 } }}>
            <Paper variant="outlined" sx={{ borderRadius: 4, p: { xs: 1.75, md: 2, xl: 2.5 } }}>
              <SectionHeader
                eyebrow="Risk View"
                title="Client Risk Snapshot"
                subtitle="Ranked by risk first so the team lead sees where intervention matters most."
              />

              <Divider sx={{ my: 2 }} />

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, px: { xs: 1, md: 1.4 } } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell align="right">Dispatched</TableCell>
                      <TableCell align="right">SLA So Far %</TableCell>
                      <TableCell align="right">Late</TableCell>
                      {livePacing ? <TableCell align="right">At Risk</TableCell> : null}
                      <TableCell align="right">Express Misses</TableCell>
                      <TableCell align="right">Standard Misses</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientRisk.length ? clientRisk.map((row) => (
                      <TableRow key={row.clientName} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                            <Typography
                              component="span"
                              sx={{ fontSize: 13, fontWeight: 600, minWidth: 0 }}
                            >
                              {row.clientName}
                            </Typography>
                          </Box>
                      </TableCell>
                        <TableCell align="right">{formatNumber(row.ordersDispatched)}</TableCell>
                        <TableCell align="right">{formatPercent(row.onTimePct)}</TableCell>
                        <TableCell align="right">{formatNumber(row.lateCount)}</TableCell>
                        {livePacing ? <TableCell align="right">{formatNumber(row.atRiskCount)}</TableCell> : null}
                        <TableCell align="right">{formatNumber(row.expressMisses)}</TableCell>
                        <TableCell align="right">{formatNumber(row.standardMisses)}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={livePacing ? 7 : 6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No client risk rows are available for this view.
                      </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 4, p: { xs: 1.75, md: 2, xl: 2.5 } }}>
              <SectionHeader
                eyebrow="Exception Queue"
                title="Late And At-Risk Orders"
                subtitle="Past-due and due-soon orders for quick follow-up."
                action={(
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportExceptionsCsv}
                    disabled={!exceptions.length}
                  >
                    Export CSV
                  </Button>
                )}
              />

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2, mb: 2 }}>
                <Chip size="small" label={`Late: ${formatNumber(summary.lateCount)}`} color={Number(summary.lateCount || 0) > 0 ? 'error' : 'default'} />
                {livePacing ? (
                  <Chip size="small" label={`At Risk: ${formatNumber(summary.atRiskCount)}`} color={Number(summary.atRiskCount || 0) > 0 ? 'warning' : 'default'} />
                ) : null}
                <Chip size="small" label={`Priority Scope: ${priority}`} />
                <Chip size="small" label={`Rows Shown: ${formatNumber(visibleExceptions.length)}`} />
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <TableContainer sx={{ maxHeight: { md: 460, xl: 520 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: 1, px: { xs: 1, md: 1.25 } } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Required Dispatch Day</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Dispatched</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Operator</TableCell>
                      {!hideSecondaryTableColumns ? <TableCell>Reason</TableCell> : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleExceptions.length ? visibleExceptions.map((row) => (
                      <TableRow key={`${row.type}-${row.orderId}`} hover>
                          <TableCell>
                          <Chip
                            size="small"
                            label={getExceptionStatusLabel(row)}
                            sx={row.type === 'AT_RISK'
                              ? { backgroundColor: 'rgba(245, 124, 0, 0.12)', color: '#ad5d00', fontWeight: 700 }
                              : { backgroundColor: 'rgba(211, 47, 47, 0.12)', color: '#b71c1c', fontWeight: 700 }}
                          />
                          </TableCell>
                        <TableCell>
                          <Tooltip title={`Order ID: ${row.orderId}`}>
                            <span>{row.orderNumber || row.orderId}</span>
                          </Tooltip>
                          </TableCell>
                        <TableCell>{row.clientName}</TableCell>
                        <TableCell>{row.requiredDispatchDay}</TableCell>
                        <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                        <TableCell>{formatDateTime(row.dispatchedDate)}</TableCell>
                        <TableCell>{row.shippingPriority || '--'}</TableCell>
                        <TableCell>{row.operator || '--'}</TableCell>
                        {!hideSecondaryTableColumns ? <TableCell>{row.lateReason || '--'}</TableCell> : null}
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={exceptionTableColumnCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No exceptions are currently in scope.
                          </TableCell>
                        </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {exceptions.length > visibleExceptions.length ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Showing the first {formatNumber(visibleExceptions.length)} rows in the table. CSV export includes all rows returned by the API.
                </Typography>
              ) : null}
        </Paper>
          </Box>

          <Paper variant="outlined" sx={{ borderRadius: 4, p: { xs: 1.75, md: 2, xl: 2.5 } }}>
            <SectionHeader
              eyebrow="Follow-Up Queue"
              title="Having Issue"
              subtitle="Orders currently marked ON HOLD, IN PROGRESS, or URGENT so management can see what needs follow-up without affecting the top KPI strip."
              action={(
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={exportHavingIssueCsv}
                  disabled={!havingIssueRows.length}
                >
                  Export CSV
                </Button>
              )}
            />

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2, mb: 2 }}>
              <Chip size="small" label={`Total: ${formatNumber(havingIssueSummary.totalCount)}`} />
              <Chip size="small" label={`On Hold: ${formatNumber(havingIssueSummary.onHoldCount)}`} color={Number(havingIssueSummary.onHoldCount || 0) > 0 ? 'error' : 'default'} />
              <Chip size="small" label={`In Progress: ${formatNumber(havingIssueSummary.inProgressCount)}`} color={Number(havingIssueSummary.inProgressCount || 0) > 0 ? 'warning' : 'default'} />
              <Chip size="small" label={`Urgent: ${formatNumber(havingIssueSummary.urgentCount)}`} color={Number(havingIssueSummary.urgentCount || 0) > 0 ? 'error' : 'default'} />
              <Chip size="small" label={`Rows Shown: ${formatNumber(visibleHavingIssueRows.length)}`} />
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <TableContainer sx={{ maxHeight: { md: 420, xl: 480 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
              <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { py: 1, px: { xs: 1, md: 1.25 } } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Required Dispatch Day</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Issue Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleHavingIssueRows.length ? visibleHavingIssueRows.map((row) => (
                    <TableRow key={`issue-${row.orderId}`} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.issueStatus || '--'}
                          sx={{
                            ...getIssueStatusChipStyles(row.issueStatus),
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Order ID: ${row.orderId}`}>
                          <span>{row.orderNumber || row.orderId}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{row.clientName}</TableCell>
                      <TableCell>{row.requiredDispatchDay || '--'}</TableCell>
                      <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                      <TableCell>{row.shippingPriority || '--'}</TableCell>
                      <TableCell>{row.issueComments || '--'}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={havingIssueTableColumnCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No active issue follow-up rows are currently in scope.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {havingIssueRows.length > visibleHavingIssueRows.length ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Showing the first {formatNumber(visibleHavingIssueRows.length)} rows in the table. CSV export includes all rows returned by the API.
              </Typography>
            ) : null}
          </Paper>
        </Stack>
      </Container>
    </Layout>
  );
}
